import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import apsLogo from "../../New APS Logo1.png";

const PHOTO_ORDINAL_MAP: Record<string, number> = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
  sixth: 6,
  seventh: 7,
  eighth: 8,
  ninth: 9,
  tenth: 10,
};

function normalizePhotoLabel(label: string) {
  const trimmed = (label || "").trim();
  if (!trimmed) return trimmed;
  const match = trimmed.toLowerCase().match(/^(\w+)\s+(photo|image|picture)s?$/);
  if (match) {
    const ordinal = PHOTO_ORDINAL_MAP[match[1]];
    if (ordinal) return `Photo ${ordinal}`;
  }
  return trimmed;
}

type ReportListRow = {
  trackTikId: number;
  uuid?: string;
  status?: string;
  severity?: string;
  submittedAt?: number;
  submittedOn?: string;
  templateName?: string;
  categoryName?: string;
  reportFlagName?: string;
  siteName?: string;
  address?: string;
  officerName?: string;
  fieldCount?: number;
  mediaCount?: number;
  lastImportStatus?: string;
  lastImportError?: string;
};

type ReportListPage = {
  reports: ReportListRow[];
  nextCursor?: string | null;
  scanned?: number;
  page?: number;
  pageSize: number;
  hasNextPage: boolean;
  totalPages?: number;
  totalCount?: number;
};

type ReportMedia = {
  mediaKey: string;
  fieldKey?: string;
  fieldLabel?: string;
  fileName?: string;
  mimeType?: string;
  sizeBytes?: number;
  downloadStatus: string;
  downloadError?: string;
  storageUrl?: string | null;
  trackTikMediaId?: string;
};

type ReportField = {
  fieldKey: string;
  fieldId?: number;
  trackTikReportFieldId?: number;
  trackTikTemplateFieldId?: number;
  trackTikFieldType?: string;
  editSupported?: boolean;
  editUnsupportedReason?: string;
  label: string;
  valueText?: string;
  valueJson?: string;
  fieldType?: string;
  required?: boolean;
  isMediaField: boolean;
  mediaKeys?: string[];
};

type ReportViewAudit = {
  id: string;
  action: string;
  actorUserId: string;
  actorEmail?: string;
  actorName?: string;
  actorImageUrl?: string;
  versionId?: string;
  versionNumber?: number;
  metadataJson?: string;
  createdAt: number;
};

type ReportVersion = {
  id: string;
  reportTrackTikId: number;
  versionNumber: number;
  source: string;
  auditLogId?: string;
  actorUserId?: string;
  actorEmail?: string;
  actorName?: string;
  actorImageUrl?: string;
  trackTikActorName?: string;
  trackTikActorEmail?: string;
  trackTikActorImageUrl?: string;
  metadataJson?: string;
  createdAt: number;
};

type ReportComment = {
  id: string;
  trackTikId?: number;
  authorId?: number;
  authorName?: string;
  authorEmail?: string;
  trackTikAuthorName?: string;
  trackTikAuthorEmail?: string;
  trackTikAuthorImageUrl?: string;
  argusAuthorUserId?: string;
  argusAuthorName?: string;
  argusAuthorEmail?: string;
  argusAuthorImageUrl?: string;
  content: string;
  type?: string;
  createdOn?: string;
  createdAt?: number;
};

type ReportSidebarTab = "comments" | "edits" | "views";

type ReportEditOption = {
  value: number;
  label: string;
  subtitle?: string;
};

type PhotoDraft = {
  operation: "add" | "replace" | "remove";
  storageId?: string;
  fileName?: string;
  mimeType?: string;
  previewUrl?: string;
};

type DateFilterMode = "default" | "today" | "week" | "month" | "custom";
type ReportStatusFilter = "ALL" | "NEW" | "PENDING" | "VERIFICATION" | "APPROVED";

const REPORT_STATUS_OPTIONS: Array<{ value: ReportStatusFilter; label: string }> = [
  { value: "ALL", label: "All Statuses" },
  { value: "NEW", label: "New Report" },
  { value: "PENDING", label: "Job Pending" },
  { value: "VERIFICATION", label: "Verification" },
  { value: "APPROVED", label: "Approved" },
];
const REPORTS_PER_PAGE = 50;

function revokePhotoDraftPreviews(drafts: Record<string, PhotoDraft>) {
  for (const draft of Object.values(drafts)) {
    if (draft.previewUrl) URL.revokeObjectURL(draft.previewUrl);
  }
}

function normalizeReportStatus(status?: string): Exclude<ReportStatusFilter, "ALL"> | "" {
  const normalized = String(status || "").trim().toUpperCase();
  if (normalized === "NEW" || normalized === "PENDING" || normalized === "VERIFICATION" || normalized === "APPROVED") {
    return normalized;
  }
  return "";
}

function reportStatusLabel(status?: string) {
  const normalized = normalizeReportStatus(status);
  return REPORT_STATUS_OPTIONS.find((option) => option.value === normalized)?.label || status || "Unknown";
}

function ReportsIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M15 3v5h4" />
      <path d="M9 12h6" />
      <path d="M9 16h6" />
      <path d="M9 8h2" />
    </svg>
  );
}

function formatDateTime(value?: number | string | null) {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateOnly(value?: number | string | null) {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString([], {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatTimeOnly(value?: number | string | null) {
  if (!value) return "No time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAuditDate(value?: number | string | null) {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString([], {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatAuditTime(value?: number | string | null) {
  if (!value) return "No time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function auditInitials(name?: string, email?: string) {
  const source = (name || email || "?").trim();
  const words = source.includes("@")
    ? source.split("@")[0].split(/[._\-\s]+/)
    : source.split(/\s+/);
  const initials = words
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("");
  return initials || "?";
}

function auditAvatarClass(value?: string) {
  const text = String(value || "");
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash + text.charCodeAt(index) * (index + 1)) % 4;
  }
  return `reports-sidebar-avatar--${hash}`;
}

function parseAuditMetadata(metadataJson?: string) {
  try {
    return metadataJson ? JSON.parse(metadataJson) : {};
  } catch {
    return {};
  }
}


function formatBytes(value?: number) {
  if (!value || !Number.isFinite(value)) return "";
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function mediaIsImage(media: ReportMedia) {
  const mime = String(media.mimeType || "").toLowerCase();
  const name = String(media.fileName || "").toLowerCase();
  return mime.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic)$/.test(name);
}

function safeFieldValue(field: ReportField) {
  if (field.valueText) return field.valueText;
  if (!field.valueJson) return "";
  try {
    const parsed = JSON.parse(field.valueJson);
    if (parsed === null || parsed === undefined || parsed === "") return "";
    if (typeof parsed === "string") return parsed;
    if (typeof parsed === "number" || typeof parsed === "boolean") return String(parsed);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return field.valueJson;
  }
}

function fieldMediaFileId(field: ReportField): number | undefined {
  if (!field.valueJson) return undefined;
  try {
    const parsed = JSON.parse(field.valueJson);
    const value = typeof parsed === "object" && parsed ? (parsed as any).fileId ?? (parsed as any).id : parsed;
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : undefined;
  } catch {
    const numeric = Number(field.valueJson);
    return Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : undefined;
  }
}

function isPhotoReportField(field: ReportField) {
  const text = `${field.trackTikFieldType || ""} ${field.fieldType || ""} ${field.label || ""}`.toLowerCase();
  return /picture|photo|image|fileimage|gallery/.test(text) && !/signature/.test(text);
}

function isSafeTextReportField(field: ReportField) {
  if (field.isMediaField || field.fieldKey.startsWith("root_")) return false;
  const text = `${field.trackTikFieldType || ""} ${field.fieldType || ""}`.toLowerCase();
  if (/relation|client|account|site|employee|flag|list_|signature|geo|location|gps|picture|photo|image|file/.test(text)) {
    return false;
  }
  return /text|textarea/.test(text);
}

function parsedFieldValue(field: ReportField): any {
  if (!field.valueJson) return undefined;
  try {
    return JSON.parse(field.valueJson);
  } catch {
    return field.valueJson;
  }
}

type TypedEditKind = "date" | "time" | "datetime" | "account" | "client" | "position" | "reportFlag";

function typedEditKind(field: ReportField): TypedEditKind | null {
  const type = `${field.trackTikFieldType || ""} ${field.fieldType || ""}`.toUpperCase();
  const label = field.label.trim().toLowerCase();
  if (field.isMediaField || /SIGNATURE|GEO|GPS|PICTURE|PHOTO|IMAGE|FILE/.test(type)) return null;
  if (field.fieldKey === "root_account") return "account";
  if (field.fieldKey === "root_position") return "position";
  if (field.fieldKey === "root_reportflag") return null;
  if (field.fieldKey === "root_reportdatetime") return "datetime";
  if (/^DATE/.test(type) || /\bDATE\b/.test(type)) return "date";
  if (/^TIME/.test(type) || /\bTIME\b/.test(type)) return "time";
  if (/REPORT_?FLAG|LIST_?FLAG|\bFLAG\b|INCIDENT/.test(type) || label.includes("incident type")) return "reportFlag";
  if (/LIST_CLIENT|CLIENT|SITE/.test(type) || label === "site") return "client";
  if (/ACCOUNT/.test(type) || label === "account" || label === "client") return "account";
  if (/POSITION/.test(type) || label === "position") return "position";
  return null;
}

function normalizeTimeInputValue(value: string) {
  const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return value.slice(0, 5);
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

function typedFieldInitialValue(field: ReportField, kind: TypedEditKind) {
  const raw = parsedFieldValue(field);
  if (kind === "date") return typeof raw === "string" ? raw.slice(0, 10) : "";
  if (kind === "time") return typeof raw === "string" ? normalizeTimeInputValue(raw) : "";
  if (kind === "datetime") {
    const value = typeof raw === "string" ? raw : field.valueText || "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.slice(0, 16);
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60_000);
    return local.toISOString().slice(0, 16);
  }
  const numeric = Number(raw);
  return Number.isFinite(numeric) && numeric > 0 ? String(Math.floor(numeric)) : "";
}

function hasMeaningfulFieldValue(field: ReportField, mediaByKey: Map<string, ReportMedia>) {
  if (safeFieldValue(field).trim()) return true;
  return (field.mediaKeys || []).some((key) => {
    const media = mediaByKey.get(key);
    return !!media && media.downloadStatus !== "failed";
  });
}

function fieldValueByLabel(fields: ReportField[], labels: string[]) {
  const normalizedLabels = labels.map((label) => label.toLowerCase());
  const field = fields.find((item) => normalizedLabels.includes(item.label.toLowerCase()));
  return field ? safeFieldValue(field) : "";
}

function normalizedInformationLabel(label: string) {
  return label.trim().replace(/:$/, "").toLowerCase();
}

function shouldShowInformationField(field: ReportField) {
  const hiddenLabels = new Set(["report date & time", "checkpoint", "position"]);
  return !hiddenLabels.has(normalizedInformationLabel(field.label));
}

function sortInformationFields(fields: ReportField[]) {
  const priority = new Map([
    ["date", 0],
    ["time", 1],
    ["account", 2],
  ]);
  return fields
    .map((field, index) => ({
      field,
      index,
      priority: priority.get(normalizedInformationLabel(field.label)) ?? 100,
    }))
    .sort((a, b) => a.priority - b.priority || a.index - b.index)
    .map((item) => item.field);
}

function dateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function reportTimestamp(report: ReportListRow) {
  const value = report.submittedAt || report.submittedOn;
  if (!value) return undefined;
  const parsed = typeof value === "number" ? value : Date.parse(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function dateRangeForFilter(mode: DateFilterMode, customStart: string, customEnd: string) {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  if (mode === "default") {
    start.setDate(start.getDate() - 7);
    return { start: start.getTime(), end: end.getTime() };
  }
  if (mode === "week") {
    const day = start.getDay();
    start.setDate(start.getDate() - day);
    return { start: start.getTime(), end: end.getTime() };
  }
  if (mode === "month") {
    start.setDate(1);
    return { start: start.getTime(), end: end.getTime() };
  }
  if (mode === "custom") {
    const customStartDate = customStart ? new Date(`${customStart}T00:00:00`) : undefined;
    const customEndDate = customEnd ? new Date(`${customEnd}T23:59:59.999`) : undefined;
    return {
      start: customStartDate && !Number.isNaN(customStartDate.getTime()) ? customStartDate.getTime() : undefined,
      end: customEndDate && !Number.isNaN(customEndDate.getTime()) ? customEndDate.getTime() : undefined,
    };
  }
  return { start: start.getTime(), end: end.getTime() };
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isInRange(day: Date, start: Date | null, end: Date | null) {
  if (!start || !end) return false;
  const t = day.getTime();
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  return t >= s && t <= e;
}

function buildMonthGrid(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: Array<{ date: Date; outside: boolean } | null> = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d), outside: false });
  while (cells.length < 42) cells.push(null);
  return cells;
}

function CalendarPicker({
  rangeStart,
  rangeEnd,
  onSelect,
  style,
}: {
  rangeStart: Date | null;
  rangeEnd: Date | null;
  onSelect: (date: Date) => void;
  style?: CSSProperties;
}) {
  const today = new Date();
  const [rightYear, setRightYear] = useState(today.getFullYear());
  const [rightMonth, setRightMonth] = useState(today.getMonth());

  const leftYear = rightMonth === 0 ? rightYear - 1 : rightYear;
  const leftMonth = rightMonth === 0 ? 11 : rightMonth - 1;

  const goBack = () => {
    if (rightMonth === 0) { setRightYear(rightYear - 1); setRightMonth(11); }
    else setRightMonth(rightMonth - 1);
  };
  const goForward = () => {
    if (rightMonth === 11) { setRightYear(rightYear + 1); setRightMonth(0); }
    else setRightMonth(rightMonth + 1);
  };

  const renderMonth = (year: number, month: number, showLeftArrow: boolean, showRightArrow: boolean) => {
    const grid = buildMonthGrid(year, month);
    return (
      <div className="reports-calendar-month">
        <div className="reports-calendar-nav">
          <button
            type="button"
            onClick={goBack}
            aria-label="Previous month"
            className={showLeftArrow ? "" : "reports-calendar-nav-hidden"}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path d="M6.5 1.5 L3 5 L6.5 8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span>{MONTH_NAMES[month]} {year}</span>
          <button
            type="button"
            onClick={goForward}
            aria-label="Next month"
            className={showRightArrow ? "" : "reports-calendar-nav-hidden"}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path d="M3.5 1.5 L7 5 L3.5 8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div className="reports-calendar-weekdays">
          {WEEKDAY_LABELS.map((d) => <span key={d}>{d}</span>)}
        </div>
        <div className="reports-calendar-grid">
          {grid.map((cell, i) => {
            if (!cell) return <div key={`e${i}`} className="reports-calendar-day reports-calendar-day--empty" />;
            const { date } = cell;
            const isToday = isSameDay(date, today);
            const isStart = rangeStart && isSameDay(date, rangeStart);
            const isEnd = rangeEnd && isSameDay(date, rangeEnd);
            const inRange = isInRange(date, rangeStart, rangeEnd);
            const classes = [
              "reports-calendar-day",
              isToday && "reports-calendar-day--today",
              inRange && "reports-calendar-day--in-range",
              isStart && "reports-calendar-day--range-start",
              isEnd && "reports-calendar-day--range-end",
            ].filter(Boolean).join(" ");
            return (
              <button
                key={date.toISOString()}
                type="button"
                className={classes}
                onMouseDown={(e) => { e.preventDefault(); onSelect(date); }}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const formatRange = (d: Date | null) => d
    ? d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
    : "---";

  return (
    <div
      className="reports-calendar-popover"
      style={style}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="reports-calendar-inner">
        {renderMonth(leftYear, leftMonth, true, false)}
        {renderMonth(rightYear, rightMonth, false, true)}
      </div>
      <div className="reports-calendar-range-label">
        <span>From: <strong>{formatRange(rangeStart)}</strong></span>
        <span>To: <strong>{formatRange(rangeEnd)}</strong></span>
      </div>
    </div>
  );
}

type SearchFacetKind = "type" | "account" | "employee";
type SearchChip = { kind: SearchFacetKind; value: string };

const SEARCH_FACET_LABELS: Record<SearchFacetKind, string> = {
  type: "Type",
  account: "Account",
  employee: "Employee",
};

const SEARCH_FACET_PLACEHOLDERS: Record<SearchFacetKind, string> = {
  type: "Pick a report type",
  account: "Pick an account",
  employee: "Pick an employee",
};

const SEARCH_FACET_TRIGGERS: Array<{ words: string[]; kind: SearchFacetKind }> = [
  { words: ["type", "types", "template", "templates", "category", "categories"], kind: "type" },
  { words: ["account", "accounts", "site", "sites", "client", "clients", "location", "locations"], kind: "account" },
  { words: ["employee", "employees", "officer", "officers", "guard", "guards", "person", "people"], kind: "employee" },
];

function detectFacetMode(text: string): { kind: SearchFacetKind; query: string } | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  for (const trigger of SEARCH_FACET_TRIGGERS) {
    for (const word of trigger.words) {
      if (lower === word) return { kind: trigger.kind, query: "" };
      if (lower.startsWith(`${word} `)) {
        return { kind: trigger.kind, query: trimmed.slice(word.length).trim() };
      }
    }
  }
  return null;
}

type ChipFilters = {
  templateName?: string;
  accountName?: string;
  officerName?: string;
};

function chipsToFilters(chips: SearchChip[]): ChipFilters {
  const filters: ChipFilters = {};
  for (const chip of chips) {
    if (chip.kind === "type") filters.templateName = chip.value;
    else if (chip.kind === "account") filters.accountName = chip.value;
    else if (chip.kind === "employee") filters.officerName = chip.value;
  }
  return filters;
}

type SleekDropdownOption<T extends string> = {
  value: T;
  label: string;
};

function SleekDropdown<T extends string>({
  value,
  options,
  onChange,
  placeholder,
  variant = "default",
  ariaLabel,
  disabled,
  className = "",
  align = "start",
}: {
  value: T | "";
  options: SleekDropdownOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  variant?: "default" | "compact";
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  align?: "start" | "end";
}) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleDoc = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current && triggerRef.current.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", handleDoc);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("mousedown", handleDoc);
      window.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const menuMaxHeight = Math.min(280, window.innerHeight - 32);
      const margin = 8;
      let top = rect.bottom + 6;
      if (top + menuMaxHeight > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - menuMaxHeight - 6);
      }
      const width = rect.width;
      const left = align === "end" ? rect.right - width : rect.left;
      setAnchor({ top, left, width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [align, open]);

  const current = options.find((option) => option.value === value);
  const displayLabel = current?.label ?? placeholder ?? "Select";

  return (
    <div
      className={`sleek-dropdown sleek-dropdown--${variant}${
        className ? ` ${className}` : ""
      }${open ? " is-open" : ""}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className="sleek-dropdown-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <span className="sleek-dropdown-label">{displayLabel}</span>
        <svg
          className="sleek-dropdown-chevron"
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M1.5 3.5 L5 7 L8.5 3.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && anchor ? (
        <div
          ref={menuRef}
          role="listbox"
          className={`sleek-dropdown-menu sleek-dropdown-menu--${variant}`}
          style={{
            position: "fixed",
            top: `${anchor.top}px`,
            left: `${anchor.left}px`,
            minWidth: `${anchor.width}px`,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`sleek-dropdown-item${
                option.value === value ? " is-active" : ""
              }`}
              onClick={(event) => {
                event.stopPropagation();
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span>{option.label}</span>
              {option.value === value ? (
                <svg
                  className="sleek-dropdown-check"
                  width="11"
                  height="11"
                  viewBox="0 0 11 11"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 5.5 L4.5 8 L9 3"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type SearchableSelectOption = {
  value: string;
  label: string;
  subtitle?: string;
};

function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  emptyLabel = "No matches",
  loading = false,
  loadingLabel = "Loading options...",
  disabled = false,
  invalid = false,
  size = "sm",
}: {
  value: string;
  options: SearchableSelectOption[] | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  loading?: boolean;
  loadingLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: "sm" | "md";
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number; flip: boolean } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const filtered = useMemo(() => {
    const list = options || [];
    const needle = query.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((option) => {
      const haystack = `${option.label} ${option.subtitle || ""}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [options, query]);

  const selected = useMemo(
    () => (options || []).find((option) => option.value === value) || null,
    [options, value]
  );

  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIndex(0);
      return;
    }
    setActiveIndex(0);
    const handle = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(handle);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleDoc = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current && triggerRef.current.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", handleDoc);
    return () => window.removeEventListener("mousedown", handleDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const margin = 8;
      const desiredHeight = Math.min(320, window.innerHeight - margin * 2);
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const flip = spaceBelow < Math.min(220, desiredHeight) && spaceAbove > spaceBelow;
      const top = flip
        ? Math.max(margin, rect.top - desiredHeight - 6)
        : rect.bottom + 6;
      setAnchor({ top, left: rect.left, width: rect.width, flip });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const child = listRef.current.children[activeIndex] as HTMLElement | undefined;
    if (child) child.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open, filtered.length]);

  const commit = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  const handleKey = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (filtered.length ? (current + 1) % filtered.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        filtered.length ? (current - 1 + filtered.length) % filtered.length : 0
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = filtered[activeIndex];
      if (option) commit(option.value);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  };

  const triggerLabel = selected
    ? selected.subtitle
      ? `${selected.label} · ${selected.subtitle}`
      : selected.label
    : loading
      ? loadingLabel
      : placeholder;

  return (
    <div
      className={`rsel${open ? " is-open" : ""}${invalid ? " is-invalid" : ""}${disabled ? " is-disabled" : ""} rsel--${size}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className="rsel-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled || (loading && !options)}
        onClick={() => setOpen((current) => !current)}
      >
        <span className={`rsel-trigger-label${selected ? "" : " is-placeholder"}`}>
          {triggerLabel}
        </span>
        <svg
          className="rsel-chevron"
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M1.5 3.5 L5 7 L8.5 3.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && anchor
        ? createPortal(
            <div
              ref={menuRef}
              className={`rsel-menu${anchor.flip ? " rsel-menu--flipped" : ""}`}
              style={{
                position: "fixed",
                top: `${anchor.top}px`,
                left: `${anchor.left}px`,
                width: `${anchor.width}px`,
                transformOrigin: anchor.flip ? "left bottom" : "left top",
              }}
              role="listbox"
              onMouseDown={(event) => event.stopPropagation()}
            >
          <div className="rsel-search">
            <svg
              className="rsel-search-icon"
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M11 11 L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              placeholder={searchPlaceholder}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleKey}
            />
            {query ? (
              <button
                type="button"
                className="rsel-search-clear"
                onClick={() => {
                  setQuery("");
                  setActiveIndex(0);
                  inputRef.current?.focus();
                }}
                aria-label="Clear search"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <path
                    d="M2 2 L8 8 M8 2 L2 8"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            ) : null}
          </div>
          <div ref={listRef} className="rsel-list">
            {loading && !options ? (
              <div className="rsel-state">{loadingLabel}</div>
            ) : filtered.length ? (
              filtered.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  className={`rsel-option${option.value === value ? " is-selected" : ""}${
                    index === activeIndex ? " is-active" : ""
                  }`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => commit(option.value)}
                >
                  <span className="rsel-option-main">
                    <span className="rsel-option-label">{option.label}</span>
                    {option.subtitle ? (
                      <span className="rsel-option-subtitle">{option.subtitle}</span>
                    ) : null}
                  </span>
                  {option.value === value ? (
                    <svg
                      className="rsel-option-check"
                      width="11"
                      height="11"
                      viewBox="0 0 11 11"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M2 5.5 L4.5 8 L9 3"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : null}
                </button>
              ))
            ) : (
              <div className="rsel-state">{emptyLabel}</div>
            )}
          </div>
        </div>,
            document.body
          )
        : null}
    </div>
  );
}

function parseIsoDate(value: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function isoFromDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function MiniCalendar({
  value,
  onSelect,
}: {
  value: string;
  onSelect: (iso: string) => void;
}) {
  const today = new Date();
  const selectedDate = useMemo(() => parseIsoDate(value), [value]);
  const [viewYear, setViewYear] = useState(
    selectedDate?.getFullYear() ?? today.getFullYear()
  );
  const [viewMonth, setViewMonth] = useState(
    selectedDate?.getMonth() ?? today.getMonth()
  );

  useEffect(() => {
    if (selectedDate) {
      setViewYear(selectedDate.getFullYear());
      setViewMonth(selectedDate.getMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const goBack = () => {
    if (viewMonth === 0) {
      setViewYear(viewYear - 1);
      setViewMonth(11);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };
  const goForward = () => {
    if (viewMonth === 11) {
      setViewYear(viewYear + 1);
      setViewMonth(0);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };
  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    onSelect(isoFromDate(today));
  };

  const grid = useMemo(
    () => buildMonthGrid(viewYear, viewMonth),
    [viewMonth, viewYear]
  );

  return (
    <div className="rep-cal">
      <div className="rep-cal-head">
        <button
          type="button"
          className="rep-cal-nav"
          onClick={goBack}
          aria-label="Previous month"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path
              d="M6.5 1.5 L3 5 L6.5 8.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <span className="rep-cal-title">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          className="rep-cal-nav"
          onClick={goForward}
          aria-label="Next month"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path
              d="M3.5 1.5 L7 5 L3.5 8.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div className="rep-cal-weekdays">
        {WEEKDAY_LABELS.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="rep-cal-grid">
        {grid.map((cell, index) => {
          if (!cell) {
            return <span key={`e${index}`} className="rep-cal-day rep-cal-day--empty" />;
          }
          const { date } = cell;
          const isToday = isSameDay(date, today);
          const isSelected = !!selectedDate && isSameDay(date, selectedDate);
          return (
            <button
              key={date.toISOString()}
              type="button"
              className={`rep-cal-day${isToday ? " is-today" : ""}${isSelected ? " is-selected" : ""}`}
              onMouseDown={(event) => {
                event.preventDefault();
                onSelect(isoFromDate(date));
              }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
      <div className="rep-cal-foot">
        <button type="button" className="rep-cal-foot-btn" onMouseDown={(e) => { e.preventDefault(); goToday(); }}>
          Today
        </button>
        <button
          type="button"
          className="rep-cal-foot-btn rep-cal-foot-btn--ghost"
          onMouseDown={(event) => {
            event.preventDefault();
            onSelect("");
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
}

function formatDateDisplay(iso: string): string {
  if (!iso) return "";
  const date = parseIsoDate(iso);
  if (!date) return "";
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const y = date.getFullYear();
  return `${m}/${d}/${y}`;
}

function parseDateInput(input: string): string {
  const cleaned = input.trim();
  if (!cleaned) return "";
  const match = cleaned.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (!match) return "";
  const mm = parseInt(match[1], 10);
  const dd = parseInt(match[2], 10);
  let yyyy = parseInt(match[3], 10);
  if (yyyy < 100) yyyy += 2000;
  if (!mm || !dd || !yyyy) return "";
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return "";
  if (yyyy < 1900 || yyyy > 2200) return "";
  const date = new Date(yyyy, mm - 1, dd);
  if (date.getMonth() !== mm - 1 || date.getDate() !== dd) return "";
  return isoFromDate(date);
}

function DateField({
  value,
  onChange,
  invalid = false,
  size = "md",
}: {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  size?: "sm" | "md";
}) {
  const [draft, setDraft] = useState(formatDateDisplay(value));
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number; flip: boolean } | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!focused) setDraft(formatDateDisplay(value));
  }, [value, focused]);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const margin = 8;
      const popHeight = 290;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const flip = spaceBelow < popHeight && spaceAbove > spaceBelow;
      const top = flip ? Math.max(margin, rect.top - popHeight - 6) : rect.bottom + 6;
      let left = rect.left;
      const popWidth = 264;
      if (left + popWidth > window.innerWidth - margin) {
        left = window.innerWidth - popWidth - margin;
      }
      if (left < margin) left = margin;
      setAnchor({ top, left, flip });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleDoc = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current && wrapRef.current.contains(target)) return;
      if (popRef.current && popRef.current.contains(target)) return;
      setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", handleDoc);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("mousedown", handleDoc);
      window.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const commitDraft = () => {
    if (!draft.trim()) {
      if (value) onChange("");
      setDraft("");
      return;
    }
    const iso = parseDateInput(draft);
    if (iso) {
      onChange(iso);
      setDraft(formatDateDisplay(iso));
    } else {
      setDraft(formatDateDisplay(value));
    }
  };

  return (
    <div ref={wrapRef} className={`rep-date rep-date--${size}${invalid ? " is-invalid" : ""}${open ? " is-open" : ""}`}>
      <input
        ref={inputRef}
        className="rep-date-input"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="mm/dd/yyyy"
        value={focused ? draft : formatDateDisplay(value)}
        onMouseDown={() => {
          setOpen(true);
        }}
        onFocus={(event) => {
          setFocused(true);
          setDraft(formatDateDisplay(value));
          setOpen(true);
          event.target.select();
        }}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          setFocused(false);
          commitDraft();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commitDraft();
            setOpen(false);
            inputRef.current?.blur();
          } else if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      />
      {open && anchor
        ? createPortal(
            <div
              ref={popRef}
              className="rep-cal-pop"
              style={{
                position: "fixed",
                top: `${anchor.top}px`,
                left: `${anchor.left}px`,
                transformOrigin: anchor.flip ? "left bottom" : "left top",
              }}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <MiniCalendar
                value={value}
                onSelect={(iso) => {
                  onChange(iso);
                  setDraft(formatDateDisplay(iso));
                  setOpen(false);
                  inputRef.current?.focus();
                }}
              />
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

function normalizeTimeText(input: string): string {
  const cleaned = input.replace(/[^\d:]/g, "");
  if (!cleaned) return "";
  let hPart = "";
  let mPart = "";
  if (cleaned.includes(":")) {
    const [h, m = ""] = cleaned.split(":");
    hPart = h;
    mPart = m;
  } else if (cleaned.length <= 2) {
    hPart = cleaned;
    mPart = "0";
  } else if (cleaned.length === 3) {
    hPart = cleaned.slice(0, 1);
    mPart = cleaned.slice(1);
  } else {
    hPart = cleaned.slice(0, 2);
    mPart = cleaned.slice(2, 4);
  }
  const hNum = Math.max(0, Math.min(23, parseInt(hPart || "0", 10) || 0));
  const mNum = Math.max(0, Math.min(59, parseInt(mPart || "0", 10) || 0));
  return `${String(hNum).padStart(2, "0")}:${String(mNum).padStart(2, "0")}`;
}

function TimeField({
  value,
  onChange,
  invalid = false,
}: {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  const adjust = (delta: number, kind: "h" | "m") => {
    const current = (focused ? draft : value).trim();
    const match = current.match(/^(\d{1,2}):?(\d{0,2})$/);
    let h = 0;
    let m = 0;
    if (match) {
      h = parseInt(match[1] || "0", 10) || 0;
      m = parseInt(match[2] || "0", 10) || 0;
    }
    if (kind === "h") {
      h = (((h + delta) % 24) + 24) % 24;
    } else {
      m = (((m + delta) % 60) + 60) % 60;
    }
    const next = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    setDraft(next);
    onChange(next);
  };

  const handleBlur = () => {
    setFocused(false);
    if (!draft.trim()) {
      if (value) onChange("");
      setDraft("");
      return;
    }
    const normalized = normalizeTimeText(draft);
    setDraft(normalized);
    onChange(normalized);
  };

  const display = focused ? draft : value;

  return (
    <input
      className={`rep-time-input${invalid ? " is-invalid" : ""}`}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder="--:--"
      maxLength={5}
      value={display}
      onFocus={(event) => {
        setFocused(true);
        setDraft(value);
        event.target.select();
      }}
      onChange={(event) => {
        const filtered = event.target.value.replace(/[^\d:]/g, "").slice(0, 5);
        setDraft(filtered);
      }}
      onBlur={handleBlur}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          (event.currentTarget as HTMLInputElement).blur();
        } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
          event.preventDefault();
          const selectionStart = (event.currentTarget as HTMLInputElement).selectionStart ?? 0;
          const colonIndex = display.indexOf(":");
          const inMinutes = colonIndex >= 0 ? selectionStart > colonIndex : false;
          adjust(event.key === "ArrowUp" ? 1 : -1, inMinutes ? "m" : "h");
        }
      }}
    />
  );
}

function DateTimeField({
  value,
  onChange,
  invalid = false,
}: {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
}) {
  const { date, time } = useMemo(() => {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})(?:T(\d{1,2}:\d{1,2}))?/);
    if (match) return { date: match[1], time: match[2] || "" };
    return { date: "", time: "" };
  }, [value]);

  const emit = (nextDate: string, nextTime: string) => {
    if (!nextDate) {
      onChange("");
      return;
    }
    const effectiveTime = nextTime || "00:00";
    onChange(`${nextDate}T${effectiveTime}`);
  };

  return (
    <div className="rep-datetime">
      <DateField
        value={date}
        invalid={invalid && !date}
        onChange={(next) => emit(next, time)}
      />
      <TimeField
        value={time}
        invalid={invalid && !!date && !time}
        onChange={(next) => emit(date, next)}
      />
    </div>
  );
}

function assignmentStatusMeta(status?: string, escalationReason?: string) {
  switch (status) {
    case "completed":
      return { label: "Completed", cls: "is-completed" };
    case "in_review":
    case "reviewing":
      return { label: "In Review", cls: "is-review" };
    case "escalated":
      return {
        label: escalationReason === "needs_photos" ? "Escalated · Photos" : "Escalated · Clarify",
        cls: "is-escalated",
      };
    case "deferred":
      return { label: "Deferred", cls: "is-deferred" };
    default:
      return { label: "New", cls: "is-new" };
  }
}

export default function ReportsPage({
  mode = "reports",
}: { mode?: "reports" | "assignments" } = {}) {
  const isAssign = mode === "assignments";
  const [searchChips, setSearchChips] = useState<SearchChip[]>([]);
  const [searchDraft, setSearchDraft] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [previewReportId, setPreviewReportId] = useState<number | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [dateFilter, setDateFilter] = useState<DateFilterMode>("default");
  const [statusFilter, setStatusFilter] = useState<ReportStatusFilter>(isAssign ? "ALL" : "NEW");
  const [assignmentView, setAssignmentView] = useState<"queue" | "handled">("queue");
  const [shiftOverride, setShiftOverride] = useState<string | null>(null);
  const [showDispatchers, setShowDispatchers] = useState(false);
  const [escalateMenuOpen, setEscalateMenuOpen] = useState(false);
  const [assignSavingStatus, setAssignSavingStatus] = useState<string | null>(null);
  const [sidebarTab, setSidebarTab] = useState<ReportSidebarTab>("comments");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<Array<string | null>>([null]);
  const [cursorFilterKey, setCursorFilterKey] = useState('');
  const [savingStatusReportId, setSavingStatusReportId] = useState<number | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [fieldDrafts, setFieldDrafts] = useState<Record<string, string>>({});
  const [photoDrafts, setPhotoDrafts] = useState<Record<string, PhotoDraft>>({});
  const [reportComments, setReportComments] = useState<ReportComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [savingPhotoFieldKey, setSavingPhotoFieldKey] = useState<string | null>(null);
  const [editMessage, setEditMessage] = useState<string | null>(null);
  const [isSavingReportEdits, setIsSavingReportEdits] = useState(false);
  const [isReformattingReport, setIsReformattingReport] = useState(false);
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(dateInputValue(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)));
  const [customEndDate, setCustomEndDate] = useState(dateInputValue(new Date()));
  const [calRangeStart, setCalRangeStart] = useState<Date | null>(null);
  const [calRangeEnd, setCalRangeEnd] = useState<Date | null>(null);
  const [calSelecting, setCalSelecting] = useState(false);
  const [previewAnchor, setPreviewAnchor] = useState<
    { top: number; left: number; placement: "right" | "left" } | null
  >(null);
  const [calendarAnchor, setCalendarAnchor] = useState<
    { top: number; left: number } | null
  >(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const dateDropdownRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const commentsThreadRef = useRef<HTMLDivElement>(null);
  const searchSuggestionsOpenRef = useRef(false);
  const activePreviewCellRef = useRef<HTMLElement | null>(null);
  const photoDraftsRef = useRef<Record<string, PhotoDraft>>({});

  useEffect(() => {
    photoDraftsRef.current = photoDrafts;
  }, [photoDrafts]);

  useEffect(() => {
    return () => revokePhotoDraftPreviews(photoDraftsRef.current);
  }, []);

  const clearPhotoDrafts = () => {
    setPhotoDrafts((current) => {
      revokePhotoDraftPreviews(current);
      return {};
    });
  };

  const computePreviewAnchor = (cellEl: HTMLElement) => {
    const rect = cellEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const popoverWidth = 330;
    const popoverHeight = Math.min(450, viewportHeight - 24);
    const margin = 12;
    const gap = 14;
    const sidebarStyle = getComputedStyle(document.documentElement)
      .getPropertyValue("--primary-sidebar-width")
      .trim();
    const sidebarWidth = Number.parseFloat(sidebarStyle) || 60;
    const minLeft = sidebarWidth + margin;
    const maxLeft = viewportWidth - popoverWidth - margin;

    let placement: "right" | "left" = "right";
    let left = rect.right + gap;
    if (left + popoverWidth > viewportWidth - margin) {
      const candidate = rect.left - popoverWidth - gap;
      if (candidate >= minLeft) {
        left = candidate;
        placement = "left";
      } else {
        left = maxLeft;
      }
    }
    left = Math.max(minLeft, Math.min(left, maxLeft));

    let top = rect.top + rect.height / 2 - popoverHeight / 2;
    if (top + popoverHeight > viewportHeight - margin) {
      top = viewportHeight - popoverHeight - margin;
    }
    if (top < margin) {
      top = margin;
    }

    setPreviewAnchor({ top, left, placement });
  };

  const reportRange = useMemo(
    () => dateRangeForFilter(dateFilter, customStartDate, customEndDate),
    [customEndDate, customStartDate, dateFilter]
  );
  const chipFilters = useMemo(() => chipsToFilters(searchChips), [searchChips]);
  const dispatchContext = useQuery(
    (api as any).assignments.getMyDispatchContext,
    isAssign ? {} : ("skip" as any)
  ) as
    | {
        isAdmin: boolean;
        assigned: boolean;
        shift: string | null;
        queueKey: string | null;
        shiftLabel: string | null;
        shifts: Array<{ key: string; label: string; queueKey: string }>;
      }
    | undefined;
  const overrideQueueKey =
    isAssign && dispatchContext?.isAdmin && shiftOverride
      ? dispatchContext.shifts?.find((s) => s.key === shiftOverride)?.queueKey || null
      : null;
  const adminFallbackQueueKey = isAssign && dispatchContext?.isAdmin ? "am-dispatch" : null;
  const effectiveQueueKey = isAssign
    ? overrideQueueKey || dispatchContext?.queueKey || adminFallbackQueueKey
    : null;
  const activeShiftLabel = isAssign
    ? dispatchContext?.shifts?.find((s) => s.queueKey === effectiveQueueKey)?.label ||
      dispatchContext?.shiftLabel ||
      null
    : null;

  const reportFilterKey = JSON.stringify([isAssign, effectiveQueueKey, assignmentView, committedSearch,
    statusFilter, reportRange.start, reportRange.end, chipFilters]);
  const cursorMatchesFilters = cursorFilterKey === reportFilterKey;
  const activeCursor = cursorMatchesFilters ? pageCursors[currentPage - 1] ?? null : null;
  const reportPageDefault = useQuery(
    (api as any).trackTikReports.listReports,
    isAssign
      ? ("skip" as any)
      : {
          limit: REPORTS_PER_PAGE,
          cursor: activeCursor,
          search: committedSearch || undefined,
          status: statusFilter === "ALL" ? undefined : statusFilter,
          submittedStart: reportRange.start,
          submittedEnd: reportRange.end,
          templateName: chipFilters.templateName,
          accountName: chipFilters.accountName,
          officerName: chipFilters.officerName,
        }
  ) as ReportListPage | undefined;
  const reportPageShift = useQuery(
    (api as any).assignments.listShiftReports,
    isAssign && effectiveQueueKey
      ? {
          queueKey: effectiveQueueKey,
          view: assignmentView,
          limit: REPORTS_PER_PAGE,
          cursor: activeCursor,
          search: committedSearch || undefined,
          submittedStart: reportRange.start,
          submittedEnd: reportRange.end,
          templateName: chipFilters.templateName,
          accountName: chipFilters.accountName,
          officerName: chipFilters.officerName,
        }
      : ("skip" as any)
  ) as ReportListPage | undefined;
  const reportPage = isAssign ? reportPageShift : reportPageDefault;
  const [stableReportPage, setStableReportPage] = useState<ReportListPage | null>(null);
  useEffect(() => {
    if (reportPage !== undefined && reportPage !== null) {
      setStableReportPage(reportPage);
    }
  }, [reportPage]);
  const effectiveReportPage = reportPage ?? (cursorMatchesFilters ? stableReportPage : null);
  const isInitialLoading = reportPage === undefined && stableReportPage === null;
  const isRefetching = reportPage === undefined && stableReportPage !== null;
  const reports = effectiveReportPage?.reports;
  const detail = useQuery(
    (api as any).trackTikReports.getReportDetail,
    selectedReportId ? { trackTikReportId: selectedReportId } : ("skip" as any)
  ) as
    | {
        report: any;
        fields: ReportField[];
        media: ReportMedia[];
      }
    | null
    | undefined;
  const previewDetail = useQuery(
    (api as any).trackTikReports.getReportDetail,
    previewReportId ? { trackTikReportId: previewReportId } : ("skip" as any)
  ) as
    | {
        report: any;
        fields: ReportField[];
        media: ReportMedia[];
      }
    | null
    | undefined;
  const viewAudit = useQuery(
    (api as any).trackTikReports.getReportViewAudit,
    selectedReportId ? { trackTikReportId: selectedReportId, limit: 500 } : ("skip" as any)
  ) as ReportViewAudit[] | undefined;
  const reportAuditLogs = useQuery(
    (api as any).trackTikReports.getReportAuditLogs,
    selectedReportId ? { trackTikReportId: selectedReportId, limit: 500 } : ("skip" as any)
  ) as ReportViewAudit[] | undefined;
  const reportVersions = useQuery(
    (api as any).trackTikReports.listReportVersions,
    selectedReportId ? { trackTikReportId: selectedReportId, limit: 500 } : ("skip" as any)
  ) as ReportVersion[] | undefined;
  const versionDetail = useQuery(
    (api as any).trackTikReports.getReportVersionDetail,
    selectedVersionId ? { versionId: selectedVersionId } : ("skip" as any)
  ) as
    | {
        version: ReportVersion;
        report: any;
        fields: ReportField[];
        media: ReportMedia[];
      }
    | null
    | undefined;
  const recordReportView = useMutation((api as any).trackTikReports.recordReportView);
  const updateReportStatus = useAction((api as any).trackTikReports.updateReportStatus);
  const updateReportFieldValue = useAction((api as any).trackTikReports.updateReportFieldValue);
  const updateReportTypedFieldValue = useAction((api as any).trackTikReports.updateReportTypedFieldValue);
  const createMissingReportFieldValue = useAction((api as any).trackTikReports.createMissingReportFieldValue);
  const generateReportPhotoUploadUrl = useMutation((api as any).trackTikReports.generateReportPhotoUploadUrl);
  const updateReportPhotoField = useAction((api as any).trackTikReports.updateReportPhotoField);
  const recordReportEditBatchSummary = useAction((api as any).trackTikReports.recordReportEditBatchSummary);
  const reformatReportDraftFields = useAction((api as any).trackTikReports.reformatReportDraftFields);
  const listReportComments = useAction((api as any).trackTikReports.listReportComments);
  const createReportComment = useAction((api as any).trackTikReports.createReportComment);
  const updateAssignmentStatus = useMutation((api as any).assignments.updateWorkItemStatus);
  const setDispatcherShift = useMutation((api as any).assignments.setDispatcherShift);
  const dispatchers = useQuery(
    (api as any).assignments.listDispatchers,
    isAssign && showDispatchers && dispatchContext?.isAdmin ? {} : ("skip" as any)
  ) as Array<{ userId: string; name?: string; email?: string; shift: string | null }> | undefined;
  const selectedWorkItem = useQuery(
    (api as any).assignments.getWorkItem,
    isAssign && selectedReportId ? { reportTrackTikId: selectedReportId } : ("skip" as any)
  ) as
    | { workStatus: string; escalationReason?: string; queueLabel?: string; routingReason?: string; claimedByName?: string }
    | null
    | undefined;
  const accountEditOptions = useQuery(
    (api as any).trackTikReports.listReportEditOptions,
    isEditMode ? { kind: "account", limit: 2000 } : ("skip" as any)
  ) as ReportEditOption[] | undefined;
  const clientEditOptions = useQuery(
    (api as any).trackTikReports.listReportEditOptions,
    isEditMode ? { kind: "client", limit: 2000 } : ("skip" as any)
  ) as ReportEditOption[] | undefined;
  const positionEditOptions = useQuery(
    (api as any).trackTikReports.listReportEditOptions,
    isEditMode ? { kind: "position", limit: 2000 } : ("skip" as any)
  ) as ReportEditOption[] | undefined;
  const reportFlagEditOptions = useQuery(
    (api as any).trackTikReports.listReportEditOptions,
    isEditMode ? { kind: "reportFlag", limit: 2000 } : ("skip" as any)
  ) as ReportEditOption[] | undefined;

  const facetMode = useMemo(() => detectFacetMode(searchDraft), [searchDraft]);
  const targetSearch = useMemo(() => {
    if (facetMode) return "";
    return searchDraft.trim();
  }, [facetMode, searchDraft]);
  const isSearchPending = targetSearch !== committedSearch;

  useEffect(() => {
    const handle = window.setTimeout(() => setCommittedSearch(targetSearch), 250);
    return () => window.clearTimeout(handle);
  }, [targetSearch]);

  useEffect(() => {
    setPageCursors([null]);
    setCursorFilterKey(reportFilterKey);
    setStableReportPage(null);
    setCurrentPage(1);
  }, [reportFilterKey]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    reportRange.end,
    reportRange.start,
    committedSearch,
    statusFilter,
    chipFilters.templateName,
    chipFilters.accountName,
    chipFilters.officerName,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [assignmentView, effectiveQueueKey]);

  useEffect(() => {
    setEscalateMenuOpen(false);
  }, [selectedReportId]);

  const handleAssignmentStatus = async (
    reportTrackTikId: number,
    workStatus: string,
    escalationReason?: string
  ) => {
    setAssignSavingStatus(escalationReason ? `${workStatus}:${escalationReason}` : workStatus);
    setEscalateMenuOpen(false);
    try {
      await updateAssignmentStatus({ reportTrackTikId, workStatus, escalationReason });
    } finally {
      setAssignSavingStatus(null);
    }
  };

  const handleAssignShift = async (
    dispatcher: { userId: string; name?: string; email?: string },
    shift: string
  ) => {
    await setDispatcherShift({
      userId: dispatcher.userId,
      email: dispatcher.email,
      name: dispatcher.name,
      shift,
    });
  };

  useEffect(() => {
    if (!effectiveReportPage?.totalPages) return;
    if (currentPage > effectiveReportPage.totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, effectiveReportPage?.totalPages]);

  useEffect(() => {
    if (!selectedReportId) return;
    void recordReportView({ trackTikReportId: selectedReportId });
  }, [recordReportView, selectedReportId]);

  useEffect(() => {
    setIsEditMode(false);
    setFieldDrafts({});
    clearPhotoDrafts();
    setSavingPhotoFieldKey(null);
    setEditMessage(null);
    setIsSavingReportEdits(false);
    setIsReformattingReport(false);
    setReportComments([]);
    setCommentsError(null);
    setCommentDraft("");
    setIsPostingComment(false);
    setSelectedVersionId(null);
    setSidebarTab("comments");
  }, [selectedReportId]);

  useEffect(() => {
    if (!selectedReportId) {
      setCommentsLoading(false);
      return;
    }
    let cancelled = false;
    setCommentsLoading(true);
    setCommentsError(null);
    void listReportComments({ reportTrackTikId: selectedReportId })
      .then((result: any) => {
        if (cancelled) return;
        setReportComments(Array.isArray(result?.comments) ? result.comments : []);
      })
      .catch((error: any) => {
        if (cancelled) return;
        setReportComments([]);
        setCommentsError(error?.message || "Unable to load TrackTik comments.");
      })
      .finally(() => {
        if (!cancelled) setCommentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listReportComments, selectedReportId]);

  useEffect(() => {
    if (sidebarTab !== "comments" || commentsLoading) return;
    const thread = commentsThreadRef.current;
    if (!thread) return;
    thread.scrollTop = thread.scrollHeight;
  }, [commentsLoading, reportComments.length, sidebarTab]);

  useEffect(() => {
    if (!previewReportId) {
      setPreviewAnchor(null);
      return;
    }
    const reposition = () => {
      const cell = activePreviewCellRef.current;
      if (cell && document.contains(cell)) {
        computePreviewAnchor(cell);
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [previewReportId]);

  useEffect(() => {
    if (!showCustomDates) {
      setCalendarAnchor(null);
      return;
    }
    const computeAnchor = () => {
      const trigger = dateDropdownRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const calendarW = 568;
      const margin = 12;
      const sidebarStyle = getComputedStyle(document.documentElement)
        .getPropertyValue("--primary-sidebar-width")
        .trim();
      const sidebarWidth = Number.parseFloat(sidebarStyle) || 60;
      const minLeft = sidebarWidth + margin;
      const maxLeft = Math.max(minLeft, viewportW - calendarW - margin);

      let left = rect.left;
      if (left + calendarW > viewportW - margin) {
        left = viewportW - calendarW - margin;
      }
      left = Math.max(minLeft, Math.min(left, maxLeft));

      const calendarH = Math.min(440, viewportH - 24);
      let top = rect.bottom + 8;
      if (top + calendarH > viewportH - margin) {
        top = Math.max(margin, rect.top - calendarH - 8);
      }
      setCalendarAnchor({ top, left });
    };
    computeAnchor();
    const handleClick = (e: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setShowCustomDates(false);
      }
    };
    window.addEventListener("mousedown", handleClick);
    window.addEventListener("scroll", computeAnchor, true);
    window.addEventListener("resize", computeAnchor);
    return () => {
      window.removeEventListener("mousedown", handleClick);
      window.removeEventListener("scroll", computeAnchor, true);
      window.removeEventListener("resize", computeAnchor);
    };
  }, [showCustomDates]);

  useEffect(() => {
    if (!isDateDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target as Node)) {
        setIsDateDropdownOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsDateDropdownOpen(false);
    };
    window.addEventListener("mousedown", handleClick);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("mousedown", handleClick);
      window.removeEventListener("keydown", handleKey);
    };
  }, [isDateDropdownOpen]);

  const handleCalendarSelect = (date: Date) => {
    if (!calSelecting || !calRangeStart) {
      setCalRangeStart(date);
      setCalRangeEnd(null);
      setCalSelecting(true);
    } else {
      const start = date < calRangeStart ? date : calRangeStart;
      const end = date < calRangeStart ? calRangeStart : date;
      setCalRangeStart(start);
      setCalRangeEnd(end);
      setCalSelecting(false);
      setCustomStartDate(dateInputValue(start));
      setCustomEndDate(dateInputValue(end));
    }
  };

  const handleStatusChange = async (report: ReportListRow, nextStatus: string) => {
    const normalized = normalizeReportStatus(nextStatus);
    if (!normalized || normalized === normalizeReportStatus(report.status)) return;
    setSavingStatusReportId(report.trackTikId);
    setStatusMessage(null);
    try {
      const result: any = await updateReportStatus({
        reportTrackTikId: report.trackTikId,
        status: normalized,
      });
      if (!result?.success) {
        setStatusMessage(result?.error || "Unable to update report status.");
      }
    } catch (error: any) {
      setStatusMessage(error?.message || "Unable to update report status.");
    } finally {
      setSavingStatusReportId((current) => (current === report.trackTikId ? null : current));
    }
  };

  const handlePostComment = async () => {
    if (!selectedReportId || isPostingComment) return;
    const comment = commentDraft.trim();
    if (!comment) return;
    setIsPostingComment(true);
    setCommentsError(null);
    try {
      const result: any = await createReportComment({
        reportTrackTikId: selectedReportId,
        comment,
      });
      if (!result?.success) {
        throw new Error(result?.error || "TrackTik did not confirm the comment.");
      }
      setReportComments(Array.isArray(result.comments) ? result.comments : []);
      setCommentDraft("");
    } catch (error: any) {
      setCommentsError(error?.message || "Unable to post TrackTik comment.");
    } finally {
      setIsPostingComment(false);
    }
  };

  const canCreateMissingField = (field: ReportField) => {
    return (
      !field.trackTikReportFieldId &&
      !!field.trackTikTemplateFieldId &&
      !field.isMediaField &&
      isSafeTextReportField(field) &&
      field.editUnsupportedReason === "missing_tracktik_report_field"
    );
  };

  const typedOptionsForKind = (kind: TypedEditKind): ReportEditOption[] | undefined => {
    if (kind === "account") return accountEditOptions;
    if (kind === "client") return clientEditOptions;
    if (kind === "position") return positionEditOptions;
    if (kind === "reportFlag") return reportFlagEditOptions;
    return undefined;
  };

  const canEditTypedField = (field: ReportField) => {
    const kind = typedEditKind(field);
    if (!kind) return false;
    if (field.editUnsupportedReason?.startsWith("ambiguous_tracktik_report_field_mapping")) return false;
    if (field.fieldKey.startsWith("root_")) {
      return ["root_account", "root_position", "root_reportdatetime"].includes(field.fieldKey);
    }
    return !!field.trackTikReportFieldId || field.editUnsupportedReason === "missing_tracktik_report_field";
  };

  const handlePhotoUpload = async (
    field: ReportField,
    event: ChangeEvent<HTMLInputElement>,
    operation: "add" | "replace"
  ) => {
    if (!selectedReportId) return;
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setSavingPhotoFieldKey(field.fieldKey);
    setEditMessage(null);
    try {
      const uploadUrl = await generateReportPhotoUploadUrl();
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!response.ok) throw new Error("Unable to upload photo to Argus storage.");
      const payload = await response.json();
      if (!payload?.storageId) throw new Error("Upload did not return a storage id.");
      setPhotoDrafts((current) => {
        if (current[field.fieldKey]?.previewUrl) URL.revokeObjectURL(current[field.fieldKey].previewUrl!);
        return {
          ...current,
          [field.fieldKey]: {
            operation,
            storageId: payload.storageId,
            fileName: file.name,
            mimeType: file.type || "application/octet-stream",
            previewUrl,
          },
        };
      });
    } catch (error: any) {
      URL.revokeObjectURL(previewUrl);
      const message = String(error?.message || "");
      setEditMessage(
        message.includes("PHOTO-EDIT-016") || message.includes("PHOTO-EDIT-018")
          ? "TrackTik rejected adding a photo into this empty slot. Replacing existing photos still works."
          : message || "Unable to stage photo."
      );
    } finally {
      setSavingPhotoFieldKey((current) => (current === field.fieldKey ? null : current));
    }
  };

  const savePhotoDraft = async (field: ReportField, draft: PhotoDraft, saveBatchId?: string) => {
    if (!selectedReportId) return;
    const expectedFileId = fieldMediaFileId(field);
    return await updateReportPhotoField({
      reportTrackTikId: selectedReportId,
      fieldKey: field.fieldKey,
      operation: draft.operation,
      storageId: draft.storageId as any,
      fileName: draft.fileName,
      mimeType: draft.mimeType,
      expectedFileId,
      saveBatchId,
    });
  };

  const handleRemovePhoto = (field: ReportField) => {
    setPhotoDrafts((current) => {
      if (current[field.fieldKey]?.previewUrl) URL.revokeObjectURL(current[field.fieldKey].previewUrl!);
      return {
        ...current,
        [field.fieldKey]: {
          operation: "remove",
        },
      };
    });
  };

  const handleClearStagedPhoto = (field: ReportField) => {
    setPhotoDrafts((current) => {
      const next = { ...current };
      const draft = next[field.fieldKey];
      if (draft?.previewUrl) URL.revokeObjectURL(draft.previewUrl);
      delete next[field.fieldKey];
      return next;
    });
  };

  const activeDetail = selectedVersionId ? versionDetail : detail;
  const isViewingHistoricalVersion = !!selectedVersionId;
  const selectedReport = activeDetail?.report;
  const mediaByKey = useMemo(() => {
    const map = new Map<string, ReportMedia>();
    for (const media of activeDetail?.media || []) {
      map.set(media.mediaKey, media);
    }
    return map;
  }, [activeDetail?.media]);

  const unattachedMedia = useMemo(() => {
    const attached = new Set<string>();
    for (const field of activeDetail?.fields || []) {
      for (const key of field.mediaKeys || []) attached.add(key);
    }
    return (activeDetail?.media || []).filter((media) => !attached.has(media.mediaKey));
  }, [activeDetail?.fields, activeDetail?.media]);

  const visibleFields = useMemo(() => {
    const fields = activeDetail?.fields || [];
    return sortInformationFields(
      fields.filter(
        (field) =>
          shouldShowInformationField(field) &&
          (field.required ||
            hasMeaningfulFieldValue(field, mediaByKey) ||
            (!isViewingHistoricalVersion && isEditMode && (field.editSupported || canCreateMissingField(field) || canEditTypedField(field) || isPhotoReportField(field))))
      )
    );
  }, [activeDetail?.fields, isEditMode, isViewingHistoricalVersion, mediaByKey]);
  const editableReportFields = useMemo(() => {
    return visibleFields.filter((field) => {
      const attachedMedia = (field.mediaKeys || [])
        .map((key) => mediaByKey.get(key))
        .filter(Boolean);
      if (attachedMedia.length > 0 || isPhotoReportField(field)) return false;
      const canEditField = (isSafeTextReportField(field) && !!field.editSupported) || canCreateMissingField(field);
      const typedKind = typedEditKind(field);
      const canEditTyped = !!typedKind && canEditTypedField(field);
      return canEditField || canEditTyped;
    });
  }, [mediaByKey, visibleFields]);
  const editablePhotoFields = useMemo(() => {
    return visibleFields.filter((field) => isPhotoReportField(field));
  }, [visibleFields]);
  const editorPhotoFields = useMemo(() => {
    const displayedKeys = new Set<string>();
    let firstEmptyField: ReportField | null = null;
    for (const field of editablePhotoFields) {
      const draft = photoDrafts[field.fieldKey];
      const hasServerMedia = (field.mediaKeys || []).some((key) => !!mediaByKey.get(key));
      const hasVisiblePhoto = draft?.operation === "remove" ? false : !!draft?.previewUrl || hasServerMedia;
      if (hasVisiblePhoto) {
        displayedKeys.add(field.fieldKey);
      } else if (!firstEmptyField) {
        firstEmptyField = field;
      }
    }
    if (firstEmptyField) displayedKeys.add(firstEmptyField.fieldKey);
    return editablePhotoFields.filter((field) => displayedKeys.has(field.fieldKey));
  }, [editablePhotoFields, mediaByKey, photoDrafts]);

  const reportFieldInitialDraft = (field: ReportField) => {
    const kind = typedEditKind(field);
    return kind ? typedFieldInitialValue(field, kind) : safeFieldValue(field);
  };

  const openReportEditor = () => {
    if (isViewingHistoricalVersion) return;
    const drafts: Record<string, string> = {};
    for (const field of editableReportFields) {
      drafts[field.fieldKey] = reportFieldInitialDraft(field);
    }
    setFieldDrafts(drafts);
    clearPhotoDrafts();
    setEditMessage(null);
    setIsEditMode(true);
  };

  const closeReportEditor = () => {
    if (isSavingReportEdits || isReformattingReport) return;
    setIsEditMode(false);
    setFieldDrafts({});
    clearPhotoDrafts();
  };

  useEffect(() => {
    if (!isEditMode) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isEditMode]);

  const changedReportEditFields = editableReportFields.filter((field) => {
    const draft = fieldDrafts[field.fieldKey] ?? reportFieldInitialDraft(field);
    return draft !== reportFieldInitialDraft(field);
  });
  const changedPhotoEditFields = editablePhotoFields.filter((field) => !!photoDrafts[field.fieldKey]);
  const changedReportEditCount = changedReportEditFields.length + changedPhotoEditFields.length;
  const requiredReportEditField = editableReportFields.find((field) => {
    const draft = fieldDrafts[field.fieldKey] ?? reportFieldInitialDraft(field);
    return !!field.required && draft.trim().length === 0;
  });
  const requiredPhotoEditField = editablePhotoFields.find((field) => {
    if (!field.required) return false;
    const draft = photoDrafts[field.fieldKey];
    if (draft?.operation === "remove") return true;
    if (draft?.previewUrl) return false;
    return !(field.mediaKeys || []).some((key) => !!mediaByKey.get(key));
  });
  const editorInformationFields = editableReportFields.filter((field) => !!typedEditKind(field));
  const editorDetailFields = editableReportFields.filter((field) => !typedEditKind(field));
  const editorPhotoTotalCount = editablePhotoFields.length;
  const editorPhotoFilledCount = editablePhotoFields.filter((field) => {
    const draft = photoDrafts[field.fieldKey];
    if (draft?.operation === "remove") return false;
    if (draft?.previewUrl) return true;
    return (field.mediaKeys || []).some((key) => !!mediaByKey.get(key));
  }).length;
  const editAuditLogs = (reportAuditLogs || []).filter((log) => {
    if (log.action === "report.viewed" || log.action.startsWith("report.comment_")) return false;
    if (log.action.includes("_attempted") || log.action.includes("_failed")) return false;
    if (log.action === "report.reformat_requested" || log.action === "report.reformat_generated") return false;
    return true;
  });
  const editHistoryEntries = useMemo(() => {
    const versionsById = new Map((reportVersions || []).map((version) => [version.id, version]));
    const entries = editAuditLogs.map((log) => {
      const metadata = parseAuditMetadata(log.metadataJson);
      const versionId = log.versionId || metadata.versionId;
      if (!versionId) return null;
      return {
        id: log.id,
        action: log.action,
        metadata,
        actorUserId: log.actorUserId,
        actorName: log.actorName,
        actorEmail: log.actorEmail,
        actorImageUrl: log.actorImageUrl,
        createdAt: log.createdAt,
        saveBatchId: metadata.saveBatchId,
        versionId,
        versionNumber: log.versionNumber || metadata.versionNumber,
        version: versionId ? versionsById.get(String(versionId)) : undefined,
      };
    }).filter(Boolean) as Array<{
      id: string;
      action: string;
      metadata: Record<string, any>;
      actorUserId?: string;
      actorName?: string;
      actorEmail?: string;
      actorImageUrl?: string;
      createdAt: number;
      saveBatchId?: string;
      versionId?: string;
      versionNumber?: number;
      version?: ReportVersion;
    }>;
    const summaryBatchIds = new Set(
      entries
        .filter((entry) => entry.action === "report.edit_batch_succeeded" && entry.saveBatchId)
        .map((entry) => String(entry.saveBatchId))
    );
    const visibleEntries = entries.filter((entry) => {
      if (entry.action === "report.edit_batch_succeeded") return true;
      return !entry.saveBatchId || !summaryBatchIds.has(String(entry.saveBatchId));
    });
    const existingVersionIds = new Set(visibleEntries.map((entry) => entry.versionId).filter(Boolean).map(String));
    for (const version of reportVersions || []) {
      if (existingVersionIds.has(String(version.id))) continue;
      visibleEntries.push({
        id: `version-${version.id}`,
        action: version.source === "before_tracktik_import" ? "report.tracktik_import_changed" : "report.version_saved",
        metadata: parseAuditMetadata(version.metadataJson),
        actorUserId: version.actorUserId,
        actorName: version.trackTikActorName || version.actorName || (version.source === "before_tracktik_import" ? "TrackTik import" : "Unknown user"),
        actorEmail: version.trackTikActorEmail || version.actorEmail,
        actorImageUrl: version.trackTikActorImageUrl || version.actorImageUrl,
        createdAt: version.createdAt,
        saveBatchId: undefined,
        versionId: version.id,
        versionNumber: version.versionNumber,
        version,
      });
    }
    const groups = new Map<string, typeof visibleEntries>();
    for (const entry of visibleEntries) {
      const key = entry.saveBatchId || `${entry.actorUserId || entry.actorEmail || entry.actorName || "unknown"}-${Math.floor(entry.createdAt / 120000)}`;
      const group = groups.get(key);
      if (group) {
        group.push(entry);
      } else {
        groups.set(key, [entry]);
      }
    }
    return Array.from(groups.values())
      .map((group) => {
        const ordered = [...group].sort((a, b) => a.createdAt - b.createdAt);
        const summary = ordered.find((entry) => entry.action === "report.edit_batch_succeeded");
        const firstVersion = ordered.find((entry) => entry.versionId) || ordered[0];
        const latest = ordered[ordered.length - 1];
        const displayEntry = summary || latest;
        return {
          ...displayEntry,
          id: group.map((entry) => entry.id).join("|"),
          versionId: displayEntry.versionId || firstVersion.versionId,
          versionNumber: displayEntry.versionNumber || firstVersion.versionNumber,
          editCount: Number(summary?.metadata?.editCount || group.length),
          createdAt: displayEntry.createdAt,
        };
      })
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [editAuditLogs, reportVersions]);
  const reportEditOptionsLoading = changedReportEditFields.some((field) => {
    const kind = typedEditKind(field);
    return !!kind && ["account", "client", "position", "reportFlag"].includes(kind) && typedOptionsForKind(kind) === undefined;
  });

  const handleSaveReportEdits = async () => {
    if (!selectedReportId) return;
    if (requiredReportEditField) {
      setEditMessage(`${requiredReportEditField.label} is required and cannot be saved empty.`);
      return;
    }
    if (requiredPhotoEditField) {
      setEditMessage(`${normalizePhotoLabel(requiredPhotoEditField.label)} is required and cannot be saved empty.`);
      return;
    }
    const fieldsToSave = changedReportEditFields;
    const photosToSave = changedPhotoEditFields;
    const totalChangesToSave = fieldsToSave.length + photosToSave.length;
    if (!totalChangesToSave) {
      setEditMessage("No report changes to save.");
      return;
    }
    setIsSavingReportEdits(true);
    setEditMessage(null);
    const saveBatchId = `${selectedReportId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let savedCount = 0;
    let batchVersionId: string | undefined;
    let batchVersionNumber: number | undefined;
    const captureBatchVersion = (result: any) => {
      if (batchVersionId || !result?.versionId) return;
      batchVersionId = String(result.versionId);
      batchVersionNumber = typeof result.versionNumber === "number" ? result.versionNumber : undefined;
    };
    try {
      for (const field of fieldsToSave) {
        const typedKind = typedEditKind(field);
        const draft = fieldDrafts[field.fieldKey] ?? reportFieldInitialDraft(field);
        if (typedKind && canEditTypedField(field)) {
          const result = await updateReportTypedFieldValue({
            reportTrackTikId: selectedReportId,
            fieldKey: field.fieldKey,
            kind: typedKind,
            newValue: draft,
            expectedValueJson: field.valueJson,
            saveBatchId,
          });
          captureBatchVersion(result);
        } else if (field.trackTikReportFieldId) {
          const result = await updateReportFieldValue({
            reportTrackTikId: selectedReportId,
            fieldKey: field.fieldKey,
            newValue: draft,
            expectedValueJson: field.valueJson,
            saveBatchId,
          });
          captureBatchVersion(result);
        } else if (canCreateMissingField(field)) {
          const result = await createMissingReportFieldValue({
            reportTrackTikId: selectedReportId,
            fieldKey: field.fieldKey,
            newValue: draft,
            confirmedTemplateFieldId: field.trackTikTemplateFieldId,
            saveBatchId,
          });
          captureBatchVersion(result);
        } else {
          throw new Error(`${field.label} is not currently editable.`);
        }
        savedCount += 1;
      }
      for (const field of photosToSave) {
        const draft = photoDrafts[field.fieldKey];
        if (!draft) continue;
        const result = await savePhotoDraft(field, draft, saveBatchId);
        captureBatchVersion(result);
        savedCount += 1;
      }
      await recordReportEditBatchSummary({
        reportTrackTikId: selectedReportId,
        saveBatchId,
        editCount: totalChangesToSave,
        versionId: batchVersionId,
        versionNumber: batchVersionNumber,
      });
      setFieldDrafts({});
      clearPhotoDrafts();
      setIsEditMode(false);
      setEditMessage(`${totalChangesToSave} change${totalChangesToSave === 1 ? "" : "s"} saved and re-imported from TrackTik.`);
    } catch (error: any) {
      const baseMessage = error?.message || "Unable to save report edits.";
      if (savedCount > 0) {
        setFieldDrafts({});
        clearPhotoDrafts();
        setIsEditMode(false);
      }
      setEditMessage(
        savedCount > 0
          ? `${savedCount} of ${totalChangesToSave} changes saved before TrackTik rejected the next change. The editor was closed so the report can refresh before another save. ${baseMessage}`
          : baseMessage
      );
    } finally {
      setIsSavingReportEdits(false);
    }
  };

  const handleReformatReport = async () => {
    if (!selectedReportId || !editorDetailFields.length) return;
    setIsReformattingReport(true);
    setEditMessage(null);
    try {
      const result = await reformatReportDraftFields({
        reportTrackTikId: selectedReportId,
        fields: editorDetailFields.map((field) => ({
          fieldKey: field.fieldKey,
          label: field.label,
          value: fieldDrafts[field.fieldKey] ?? reportFieldInitialDraft(field),
        })),
      });
      const suggestions = Array.isArray(result?.fields) ? result.fields : [];
      if (!suggestions.length) throw new Error("No report text was returned.");
      setFieldDrafts((current) => {
        const next = { ...current };
        for (const suggestion of suggestions) {
          if (typeof suggestion?.fieldKey === "string" && typeof suggestion?.value === "string") {
            next[suggestion.fieldKey] = suggestion.value;
          }
        }
        return next;
      });
      setEditMessage("Report text reformatted. Review the draft before saving.");
    } catch (error: any) {
      setEditMessage(error?.message || "Unable to reformat report text.");
    } finally {
      setIsReformattingReport(false);
    }
  };

  const reportDateValue = selectedReport?.reportTime || selectedReport?.submittedAt || selectedReport?.submittedOn;
  const openedByScanning = fieldValueByLabel(activeDetail?.fields || [], ["Opened by scanning"]);
  const filteredReports = useMemo(() => {
    return (reports || []).filter((report) => {
      if (!isAssign && statusFilter !== "ALL" && normalizeReportStatus(report.status) !== statusFilter) return false;
      const timestamp = reportTimestamp(report);
      if (!timestamp) return isAssign;
      if (reportRange.start !== undefined && timestamp < reportRange.start) return false;
      if (reportRange.end !== undefined && timestamp > reportRange.end) return false;
      return true;
    });
  }, [isAssign, reportRange.end, reportRange.start, reports, statusFilter]);
  const activePreviewRow = useMemo(
    () =>
      previewReportId
        ? filteredReports.find((report) => report.trackTikId === previewReportId) || null
        : null,
    [filteredReports, previewReportId]
  );
  const activePreview = useMemo(() => {
    if (!activePreviewRow) return null;
    const detailMatches =
      previewDetail?.report?.trackTikId === activePreviewRow.trackTikId;
    const previewFields: ReportField[] = detailMatches ? previewDetail?.fields || [] : [];
    const previewMedia: ReportMedia[] = detailMatches ? previewDetail?.media || [] : [];
    const previewReport = detailMatches ? previewDetail?.report : null;
    const previewDate =
      previewReport?.reportTime ||
      previewReport?.submittedAt ||
      activePreviewRow.submittedAt ||
      activePreviewRow.submittedOn;
    const previewType =
      previewReport?.templateName ||
      previewReport?.categoryName ||
      activePreviewRow.templateName ||
      activePreviewRow.categoryName ||
      "TrackTik Report";
    const accountName =
      previewReport?.accountName ||
      previewReport?.siteName ||
      activePreviewRow.siteName ||
      "Not recorded";
    const officerName =
      previewReport?.officerName || activePreviewRow.officerName || "Unknown officer";

    const previewMediaByKey = new Map(previewMedia.map((media) => [media.mediaKey, media]));
    const descriptionKeywords = [
      "description",
      "narrative",
      "notes",
      "comments",
      "summary",
      "details",
      "observation",
      "remarks",
    ];
    const isLikelyDescriptionLabel = (rawLabel: string) => {
      const label = rawLabel.toLowerCase();
      return descriptionKeywords.some((keyword) => label.includes(keyword));
    };
    const previewDescriptionField =
      previewFields.find((field) => {
        if (field.isMediaField || (field.mediaKeys || []).length > 0) return false;
        if (!isLikelyDescriptionLabel(field.label)) return false;
        return safeFieldValue(field).trim().length > 0;
      }) ||
      previewFields.find((field) => {
        if (field.isMediaField || (field.mediaKeys || []).length > 0) return false;
        return safeFieldValue(field).trim().length > 80;
      });
    const previewDescription = previewDescriptionField
      ? safeFieldValue(previewDescriptionField).trim()
      : "";

    const reservedLabels = new Set([
      "account",
      "site",
      "client",
      "reported by",
      "created by",
      "date",
      "time",
    ]);
    const seenLabels = new Set(reservedLabels);
    const previewVisibleFields = sortInformationFields(
      previewFields.filter((field) => {
        if (
          previewDescriptionField &&
          field.fieldKey === previewDescriptionField.fieldKey
        ) {
          return false;
        }
        const label = normalizedInformationLabel(field.label);
        const hasAttachedMedia = (field.mediaKeys || []).length > 0;
        if (
          !shouldShowInformationField(field) ||
          !hasMeaningfulFieldValue(field, previewMediaByKey) ||
          seenLabels.has(label) ||
          field.isMediaField ||
          hasAttachedMedia ||
          /^photo\b|^image\b|^signature\b|^attachment\b/i.test(field.label.trim())
        ) {
          return false;
        }
        const value = safeFieldValue(field).trim();
        if (!value) return false;
        if (/^\d{4,}$/.test(value)) return false;
        seenLabels.add(label);
        return true;
      })
    ).slice(0, 5);

    const previewImages = previewMedia.filter(mediaIsImage).slice(0, 2);
    const isLoading = !detailMatches;

    return {
      reportId: activePreviewRow.trackTikId,
      previewType,
      previewDate,
      accountName,
      officerName,
      previewVisibleFields,
      previewDescription,
      previewImages,
      isLoading,
    };
  }, [activePreviewRow, previewDetail]);
  const usedFacetKinds = useMemo(
    () => new Set(searchChips.map((chip) => chip.kind)),
    [searchChips]
  );
  const activeFacet =
    facetMode && !usedFacetKinds.has(facetMode.kind) ? facetMode.kind : null;
  const facetQuery = activeFacet ? facetMode!.query : "";

  const facetData = useQuery(
    (api as any).trackTikReports.getReportFacets,
    activeFacet
      ? {
          kind: activeFacet,
          status: statusFilter === "ALL" ? undefined : statusFilter,
          submittedStart: reportRange.start,
          submittedEnd: reportRange.end,
          limit: 5000,
          templateName: chipFilters.templateName,
          accountName: chipFilters.accountName,
          officerName: chipFilters.officerName,
        }
      : ("skip" as any)
  ) as { values: string[]; truncated: boolean } | undefined;
  const shouldSearchFacetValues = !activeFacet && searchDraft.trim().length > 0;
  const typeFacetData = useQuery(
    (api as any).trackTikReports.getReportFacets,
    shouldSearchFacetValues && !usedFacetKinds.has("type")
      ? {
          kind: "type",
          status: statusFilter === "ALL" ? undefined : statusFilter,
          submittedStart: reportRange.start,
          submittedEnd: reportRange.end,
          limit: 5000,
          templateName: chipFilters.templateName,
          accountName: chipFilters.accountName,
          officerName: chipFilters.officerName,
        }
      : ("skip" as any)
  ) as { values: string[]; truncated: boolean } | undefined;
  const accountFacetData = useQuery(
    (api as any).trackTikReports.getReportFacets,
    shouldSearchFacetValues && !usedFacetKinds.has("account")
      ? {
          kind: "account",
          status: statusFilter === "ALL" ? undefined : statusFilter,
          submittedStart: reportRange.start,
          submittedEnd: reportRange.end,
          limit: 5000,
          templateName: chipFilters.templateName,
          accountName: chipFilters.accountName,
          officerName: chipFilters.officerName,
        }
      : ("skip" as any)
  ) as { values: string[]; truncated: boolean } | undefined;
  const employeeFacetData = useQuery(
    (api as any).trackTikReports.getReportFacets,
    shouldSearchFacetValues && !usedFacetKinds.has("employee")
      ? {
          kind: "employee",
          status: statusFilter === "ALL" ? undefined : statusFilter,
          submittedStart: reportRange.start,
          submittedEnd: reportRange.end,
          limit: 5000,
          templateName: chipFilters.templateName,
          accountName: chipFilters.accountName,
          officerName: chipFilters.officerName,
        }
      : ("skip" as any)
  ) as { values: string[]; truncated: boolean } | undefined;

  type SearchSuggestion =
    | { type: "chip"; kind: SearchFacetKind; value: string; label: string }
    | { type: "category"; kind: SearchFacetKind };

  const searchSuggestions = useMemo<SearchSuggestion[]>(() => {
    const draft = searchDraft.trim();
    const draftLower = draft.toLowerCase();
    const facetQueryLower = facetQuery.toLowerCase();

    if (activeFacet) {
      const values = facetData?.values || [];
      const filtered = facetQueryLower
        ? values.filter((value) => value.toLowerCase().includes(facetQueryLower))
        : values;
      return filtered.slice(0, 200).map<SearchSuggestion>((value) => ({
        type: "chip",
        kind: activeFacet,
        value,
        label: value,
      }));
    }

    if (!draft) {
      const remaining: SearchFacetKind[] = ["type", "account", "employee"];
      return remaining
        .filter((kind) => !usedFacetKinds.has(kind))
        .map<SearchSuggestion>((kind) => ({ type: "category", kind }));
    }

    const facetMatches: SearchSuggestion[] = [];
    const seen = new Set<string>();
    const addFacetMatches = (
      kind: SearchFacetKind,
      data: { values: string[]; truncated: boolean } | undefined
    ) => {
      if (usedFacetKinds.has(kind)) return;
      for (const value of data?.values || []) {
        if (!value.toLowerCase().includes(draftLower)) continue;
        const key = `${kind}:${value.toLowerCase()}`;
        if (seen.has(key)) continue;
        seen.add(key);
        facetMatches.push({ type: "chip", kind, value, label: value });
        if (facetMatches.length >= 24) return;
      }
    };
    addFacetMatches("type", typeFacetData);
    addFacetMatches("account", accountFacetData);
    addFacetMatches("employee", employeeFacetData);
    if (facetMatches.length > 0) return facetMatches;

    return SEARCH_FACET_TRIGGERS
      .filter(({ kind, words }) => {
        if (usedFacetKinds.has(kind)) return false;
        return words.some((word) => word.includes(draftLower) || draftLower.includes(word));
      })
      .map<SearchSuggestion>(({ kind }) => ({ type: "category", kind }));
  }, [
    activeFacet,
    accountFacetData,
    employeeFacetData,
    facetData,
    facetQuery,
    searchDraft,
    typeFacetData,
    usedFacetKinds,
  ]);

  useEffect(() => {
    setHighlightedSuggestion(0);
  }, [searchDraft, activeFacet, searchChips.length]);

  const addChip = (chip: SearchChip) => {
    setSearchChips((prev) => {
      if (prev.some((c) => c.kind === chip.kind)) return prev;
      return [...prev, chip];
    });
    setSearchDraft("");
    setHighlightedSuggestion(0);
  };

  const commitSuggestion = (suggestion: SearchSuggestion) => {
    if (suggestion.type === "chip") {
      addChip({ kind: suggestion.kind, value: suggestion.value });
      return;
    }
    const triggerWord = SEARCH_FACET_TRIGGERS.find((t) => t.kind === suggestion.kind)!.words[0];
    setSearchDraft(triggerWord);
    setHighlightedSuggestion(0);
    searchInputRef.current?.focus();
  };

  const firstChipSuggestionIndex = searchSuggestions.findIndex(
    (suggestion) => suggestion.type === "chip"
  );
  const hasFilterText =
    (activeFacet ? facetQuery.length > 0 : searchDraft.trim().length > 0);

  const tryCommitChipSuggestion = (preferHighlighted: boolean) => {
    if (searchSuggestions.length === 0) return false;
    const indices = preferHighlighted
      ? [highlightedSuggestion, firstChipSuggestionIndex]
      : [firstChipSuggestionIndex, highlightedSuggestion];
    for (const index of indices) {
      if (index < 0 || index >= searchSuggestions.length) continue;
      const target = searchSuggestions[index];
      if (target.type === "chip") {
        commitSuggestion(target);
        return true;
      }
    }
    return false;
  };

  const handleSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      // If user has typed real filter text, commit the highlighted suggestion.
      if (hasFilterText && tryCommitChipSuggestion(true)) {
        event.preventDefault();
        return;
      }
      // Otherwise: just dismiss and let the existing committed search apply.
      event.preventDefault();
      setIsSearchFocused(false);
      searchInputRef.current?.blur();
      return;
    }
    if (event.key === "Tab") {
      // Pick first/highlighted chip suggestion if available, like clicking it.
      if (tryCommitChipSuggestion(true)) {
        event.preventDefault();
        return;
      }
      // No chip to commit -> let Tab move focus naturally.
      return;
    }
    if (event.key === ",") {
      if (hasFilterText && tryCommitChipSuggestion(true)) {
        event.preventDefault();
      }
      return;
    }
    if (event.key === "Backspace" && !searchDraft && searchChips.length > 0) {
      event.preventDefault();
      setSearchChips((prev) => prev.slice(0, -1));
      return;
    }
    if (event.key === "ArrowDown") {
      if (searchSuggestions.length === 0) return;
      event.preventDefault();
      setHighlightedSuggestion((prev) => (prev + 1) % searchSuggestions.length);
      return;
    }
    if (event.key === "ArrowUp") {
      if (searchSuggestions.length === 0) return;
      event.preventDefault();
      setHighlightedSuggestion((prev) =>
        prev === 0 ? searchSuggestions.length - 1 : prev - 1
      );
      return;
    }
    if (event.key === "Escape") {
      setIsSearchFocused(false);
      searchInputRef.current?.blur();
    }
  };

  const isSuggestionsOpen = isSearchFocused && searchSuggestions.length > 0;
  useEffect(() => {
    searchSuggestionsOpenRef.current = isSuggestionsOpen;
  }, [isSuggestionsOpen]);

  useEffect(() => {
    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (!searchSuggestionsOpenRef.current) return;
      const container = searchContainerRef.current;
      if (container && container.contains(event.target as Node)) return;

      event.preventDefault();
      event.stopPropagation();
      setIsSearchFocused(false);
      searchInputRef.current?.blur();

      const swallowNextClick = (clickEvent: Event) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        clickEvent.stopImmediatePropagation();
      };
      window.addEventListener("click", swallowNextClick, { capture: true, once: true });
      window.setTimeout(() => {
        window.removeEventListener("click", swallowNextClick, true);
      }, 300);
    };
    window.addEventListener("pointerdown", handleOutsidePointerDown, true);
    return () => {
      window.removeEventListener("pointerdown", handleOutsidePointerDown, true);
    };
  }, []);
  const searchPlaceholder = activeFacet
    ? SEARCH_FACET_PLACEHOLDERS[activeFacet]
    : searchChips.length > 0
      ? "Add another filter..."
      : "Search by type, employee, or account";

  return (
    <div className="reports-page">
      <div className={`reports-workspace ${selectedReportId ? "reports-workspace--detail" : "reports-workspace--list"}`}>
        {!selectedReportId ? (
        <main className="reports-directory-panel">
          <div className="reports-directory-toolbar">
            <div className="reports-directory-actions">
              <div className="reports-search-card">
                <div className="reports-search-label">Search reports</div>
                <div
                  ref={searchContainerRef}
                  className={`reports-smart-search reports-smart-search--chips${
                    isSearchFocused ? " is-focused" : ""
                  }${searchChips.length > 0 ? " has-chips" : ""}`}
                  onMouseDown={(event) => {
                    if (event.target === event.currentTarget) {
                      event.preventDefault();
                      searchInputRef.current?.focus();
                    }
                  }}
                >
                  {searchChips.map((chip) => (
                    <span
                      key={chip.kind}
                      className={`reports-search-chip reports-search-chip--${chip.kind}`}
                    >
                      <span className="reports-search-chip-kind">
                        {SEARCH_FACET_LABELS[chip.kind]}
                      </span>
                      <span className="reports-search-chip-value">
                        {chip.value}
                      </span>
                    </span>
                  ))}
                  <input
                    ref={searchInputRef}
                    className="reports-search-chip-input"
                    value={searchDraft}
                    size={
                      searchChips.length === 0
                        ? undefined
                        : Math.max(6, searchDraft.length + 1)
                    }
                    onBlur={() => window.setTimeout(() => setIsSearchFocused(false), 140)}
                    onChange={(event) => setSearchDraft(event.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={searchChips.length === 0 ? searchPlaceholder : ""}
                    aria-label="Search reports"
                  />
                  {searchChips.length > 0 || searchDraft.length > 0 ? (
                    <button
                      type="button"
                      className="reports-search-clear-all"
                      aria-label="Clear search"
                      title="Clear search"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setSearchChips([]);
                        setSearchDraft("");
                        setHighlightedSuggestion(0);
                        searchInputRef.current?.focus();
                      }}
                    >
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                        <path
                          d="M2 2 L8 8 M8 2 L2 8"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  ) : null}
                  {isSuggestionsOpen ? (
                    <div
                      className={`reports-search-suggestions${
                        activeFacet ? " reports-search-suggestions--facet" : ""
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                    >
                      {activeFacet ? (
                        <div className="reports-search-suggestions-header">
                          <span>Pick {SEARCH_FACET_LABELS[activeFacet].toLowerCase()}</span>
                          {facetData?.truncated ? <span>Suggestions cover a limited sample. Use text search for other matches.</span> : null}
                        </div>
                      ) : null}
                      {!activeFacet && searchDraft.trim().length === 0 && searchSuggestions.length > 0 ? (
                        <div className="reports-search-suggestions-header">
                          <span>Search by</span>
                        </div>
                      ) : null}
                      <div className="reports-search-suggestions-list">
                        {searchSuggestions.map((suggestion, index) => {
                          const key =
                            suggestion.type === "chip"
                              ? `${suggestion.kind}:${suggestion.value}`
                              : `category:${suggestion.kind}`;
                          const kindLabel = SEARCH_FACET_LABELS[suggestion.kind];
                          const isActive = index === highlightedSuggestion;
                          if (suggestion.type === "category") {
                            return (
                              <button
                                key={key}
                                type="button"
                                data-kind={suggestion.kind}
                                className={`reports-search-suggestion is-category${
                                  isActive ? " is-active" : ""
                                }`}
                                onMouseEnter={() => setHighlightedSuggestion(index)}
                                onClick={() => commitSuggestion(suggestion)}
                              >
                                <span>{kindLabel}</span>
                                <strong>Browse all {kindLabel.toLowerCase()}s</strong>
                              </button>
                            );
                          }
                          return (
                            <button
                              key={key}
                              type="button"
                              data-kind={suggestion.kind}
                              className={`reports-search-suggestion${
                                isActive ? " is-active" : ""
                              }`}
                              onMouseEnter={() => setHighlightedSuggestion(index)}
                              onClick={() => commitSuggestion(suggestion)}
                            >
                              <span>{kindLabel}</span>
                              <strong>{suggestion.label}</strong>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              {isAssign ? (
                <div className="reports-assign-controls">
                  <div className="reports-search-label">
                    {activeShiftLabel || "Your shift"}
                  </div>
                  <div className="reports-assign-row">
                    <div className="reports-assign-tabs" role="tablist" aria-label="Assignment view">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={assignmentView === "queue"}
                        className={`reports-assign-tab${assignmentView === "queue" ? " is-active" : ""}`}
                        onClick={() => setAssignmentView("queue")}
                      >
                        Queue
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={assignmentView === "handled"}
                        className={`reports-assign-tab${assignmentView === "handled" ? " is-active" : ""}`}
                        onClick={() => setAssignmentView("handled")}
                      >
                        Handled
                      </button>
                    </div>
                    {dispatchContext?.isAdmin ? (
                      <div className="reports-assign-admin">
                        <SleekDropdown<string>
                          value={shiftOverride || dispatchContext.shift || "am"}
                          options={(dispatchContext.shifts || []).map((s) => ({ value: s.key, label: s.label }))}
                          onChange={(next) => setShiftOverride(next)}
                          variant="compact"
                          ariaLabel="View shift"
                        />
                        <button
                          type="button"
                          className="reports-assign-manage"
                          onClick={() => setShowDispatchers(true)}
                        >
                          Manage
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="reports-status-filter">
                  <div className="reports-search-label">Status filter</div>
                  <SleekDropdown<ReportStatusFilter>
                    value={statusFilter}
                    options={REPORT_STATUS_OPTIONS}
                    onChange={(next) => setStatusFilter(next)}
                    ariaLabel="Filter reports by status"
                  />
                </div>
              )}
              <div className="reports-date-filter">
                <div className="reports-search-label">Date filter</div>
                {(() => {
                  const dateModes: Array<[DateFilterMode, string]> = [
                    ["default", "Default"],
                    ["today", "Today"],
                    ["week", "This Week"],
                    ["month", "This Month"],
                    ["custom", "Custom"],
                  ];
                  const formatRangeDate = (date: Date) =>
                    date.toLocaleDateString([], { month: "long", day: "numeric" });
                  let currentLabel = dateModes.find(([mode]) => mode === dateFilter)?.[1] ?? "Select range";
                  if (dateFilter === "default") {
                    const start = new Date(reportRange.start || Date.now());
                    const end = new Date(reportRange.end || Date.now());
                    currentLabel = `${formatRangeDate(start)} - ${formatRangeDate(end)}`;
                  }
                  if (dateFilter === "custom" && calRangeStart && calRangeEnd) {
                    currentLabel = `${formatRangeDate(calRangeStart)} - ${formatRangeDate(calRangeEnd)}`;
                  }
                  return (
                    <div className="reports-date-dropdown" ref={dateDropdownRef}>
                      <button
                        type="button"
                        className={`reports-date-trigger${isDateDropdownOpen ? " is-open" : ""}`}
                        aria-haspopup="listbox"
                        aria-expanded={isDateDropdownOpen}
                        onClick={() => setIsDateDropdownOpen((open) => !open)}
                      >
                        <span>{currentLabel}</span>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                          <path
                            d="M1.5 3.5 L5 7 L8.5 3.5"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      {isDateDropdownOpen ? (
                        <div className="reports-date-menu" role="listbox">
                          {dateModes.map(([mode, label]) => (
                            <button
                              key={mode}
                              type="button"
                              role="option"
                              aria-selected={dateFilter === mode}
                              className={`reports-date-menu-item${dateFilter === mode ? " is-active" : ""}`}
                              onClick={() => {
                                setDateFilter(mode);
                                setShowCustomDates(mode === "custom");
                                setIsDateDropdownOpen(false);
                              }}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
                {showCustomDates && calendarAnchor ? (
                  <div ref={calendarRef}>
                    <CalendarPicker
                      rangeStart={calRangeStart}
                      rangeEnd={calRangeEnd}
                      onSelect={handleCalendarSelect}
                      style={{ top: calendarAnchor.top, left: calendarAnchor.left }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          {statusMessage ? <div className="reports-status-message">{statusMessage}</div> : null}
          <div className="reports-report-table">
            <div className="reports-table-header">
              <span>Preview</span>
              <span>Type</span>
              <span>Date</span>
              <span>Reported by</span>
              <span>Account</span>
              <span>Status</span>
              <span></span>
            </div>
            <div
              className={`reports-list reports-list--directory${
                isSearchPending || isRefetching ? " reports-list--loading" : ""
              }`}
            >
              {isAssign && dispatchContext !== undefined && !effectiveQueueKey ? (
                <div className="reports-list-state">
                  You don't have a dispatch shift assigned yet. Ask an admin to assign your shift.
                </div>
              ) : isInitialLoading ? (
                <div className="reports-list-state reports-list-state--loading">
                  <span className="reports-loading-pulse" aria-hidden="true" />
                  <span>Loading reports</span>
                </div>
              ) : reports === undefined || reports.length === 0 ? (
                <div className="reports-list-state">
                  {isAssign
                    ? assignmentView === "handled"
                      ? "No handled reports yet for this shift."
                      : "No reports in this shift's queue right now."
                    : "No reports match the selected filters."}
                </div>
              ) : filteredReports.length === 0 ? (
                <div className="reports-list-state">
                  No reports match the selected filters.
                </div>
              ) : (
                filteredReports.map((report) => {
                  const rowStatus = normalizeReportStatus(report.status);
                  return (
                    <div
                      key={report.trackTikId}
                      role="button"
                      tabIndex={0}
                      className="reports-table-row"
                      onClick={() => setSelectedReportId(report.trackTikId)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedReportId(report.trackTikId);
                        }
                      }}
                    >
                      <span
                        className="reports-preview-cell"
                        onBlur={() => {
                          setPreviewReportId((current) =>
                            current === report.trackTikId ? null : current
                          );
                          activePreviewCellRef.current = null;
                        }}
                        onFocus={(event) => {
                          setPreviewReportId(report.trackTikId);
                          activePreviewCellRef.current = event.currentTarget;
                          computePreviewAnchor(event.currentTarget);
                        }}
                        onMouseEnter={(event) => {
                          setPreviewReportId(report.trackTikId);
                          activePreviewCellRef.current = event.currentTarget;
                          computePreviewAnchor(event.currentTarget);
                        }}
                        onMouseLeave={() => {
                          setPreviewReportId((current) =>
                            current === report.trackTikId ? null : current
                          );
                          activePreviewCellRef.current = null;
                        }}
                      >
                        <span
                          className="reports-preview-trigger"
                          aria-label={`Preview report ${report.trackTikId}`}
                        >
                          <ReportsIcon />
                        </span>
                      </span>
                      <strong>
                        {report.templateName || report.categoryName || "TrackTik Report"}
                      </strong>
                      <span>{formatDateTime(report.submittedAt || report.submittedOn)}</span>
                      <span>{report.officerName || "Unknown officer"}</span>
                      <span>{report.siteName || "Unknown account"}</span>
                      <span
                        className="reports-status-cell"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        {isAssign ? (
                          (() => {
                            const meta = assignmentStatusMeta(
                              (report as any).assignmentStatus,
                              (report as any).escalationReason
                            );
                            return (
                              <span className={`reports-assign-pill ${meta.cls}`}>{meta.label}</span>
                            );
                          })()
                        ) : (
                          <SleekDropdown<Exclude<ReportStatusFilter, "ALL">>
                            value={rowStatus}
                            options={REPORT_STATUS_OPTIONS.filter(
                              (option) => option.value !== "ALL"
                            ) as Array<{
                              value: Exclude<ReportStatusFilter, "ALL">;
                              label: string;
                            }>}
                            placeholder={reportStatusLabel(report.status)}
                            variant="compact"
                            disabled={savingStatusReportId === report.trackTikId}
                            ariaLabel={`Status for report ${report.trackTikId}`}
                            onChange={(next) => {
                              void handleStatusChange(report, next);
                            }}
                          />
                        )}
                      </span>
                      <span className="reports-row-action">View</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          {effectiveReportPage && (
            <div className="reports-pagination" aria-label="Report pages">
              <span className="reports-pagination-summary">
                Page {currentPage}: {reports?.length ?? 0} reports
                {(reports?.length ?? 0) === 0 && effectiveReportPage.hasNextPage ? ' - no matches here; more pages are available' : ''}
              </span>
              <div className="reports-pagination-controls">
                <button type="button" className="reports-page-button" disabled={currentPage === 1 || isRefetching}
                  onClick={() => setCurrentPage(page => Math.max(1, page - 1))}>Previous</button>
                <button type="button" className="reports-page-button"
                  disabled={isRefetching || !effectiveReportPage.hasNextPage || !effectiveReportPage.nextCursor}
                  onClick={() => {
                    if (!effectiveReportPage.nextCursor) return;
                    setPageCursors(previous => [...previous.slice(0, currentPage), effectiveReportPage.nextCursor!]);
                    setCurrentPage(page => page + 1);
                  }}>Next</button>
              </div>
            </div>
          )}
        </main>
        ) : (
        <aside className="reports-audit-panel">
          <button type="button" className="reports-back-button" onClick={() => setSelectedReportId(null)}>
            BACK TO REPORTS
          </button>
          <div className="reports-sidebar-tabs" role="tablist" aria-label="Report activity">
            {([
              ["comments", "Comments", reportComments.length],
              ["edits", "Edits", editHistoryEntries.length],
              ["views", "Views", viewAudit?.length || 0],
            ] as Array<[ReportSidebarTab, string, number]>).map(([tab, label, count]) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={sidebarTab === tab}
                className={`reports-sidebar-tab${sidebarTab === tab ? " is-active" : ""}`}
                onClick={() => setSidebarTab(tab)}
              >
                <span className="reports-sidebar-tab-icon" aria-hidden="true">
                  {tab === "views" ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" stroke="currentColor" strokeWidth="1.7" />
                      <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.7" />
                    </svg>
                  ) : tab === "edits" ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M4 20h4l11-11a2.6 2.6 0 0 0-4-4L4 16v4Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M4 5h16v11H8l-4 4V5Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                {label}
                <span className="reports-sidebar-tab-count">{count}</span>
              </button>
            ))}
          </div>
          <div className={`reports-sidebar-panel${sidebarTab === "comments" ? " reports-sidebar-panel--comments" : ""}`}>
            {sidebarTab === "views" ? (
              viewAudit === undefined ? (
                <div className="reports-empty">Loading views...</div>
              ) : viewAudit.length === 0 ? (
                <div className="reports-empty">No recorded views yet.</div>
              ) : (
                viewAudit.map((view) => {
                  const person = view.actorName || view.actorEmail || "Unknown user";
                  return (
                    <div key={view.id} className="reports-sidebar-view-row">
                      <div className="reports-sidebar-view-row-avatar-wrap">
                        {view.actorImageUrl ? (
                          <img src={view.actorImageUrl} alt="" className="reports-sidebar-avatar-img" />
                        ) : (
                          <span className={`${auditAvatarClass(person)} reports-sidebar-view-row-avatar`}>
                            {auditInitials(view.actorName, view.actorEmail)}
                          </span>
                        )}
                      </div>
                      <div className="reports-sidebar-view-row-copy">
                        <p>
                          <strong>{person}</strong>{" "}
                          <span>viewed the report</span>
                        </p>
                        <time>
                          {formatAuditTime(view.createdAt)} · {formatAuditDate(view.createdAt)}
                        </time>
                      </div>
                    </div>
                  );
                })
              )
            ) : sidebarTab === "edits" ? (
              reportAuditLogs === undefined ? (
                <div className="reports-empty">Loading edits...</div>
              ) : editHistoryEntries.length === 0 ? (
                <div className="reports-sidebar-card reports-sidebar-card--comment-empty">
                  <p>No edits yet.</p>
                  <span>When this report is edited, the previous version is saved as a backup and listed here.</span>
                </div>
              ) : (
                editHistoryEntries.map((entry) => {
                  const person = entry.actorName || entry.actorEmail || "TrackTik import";
                  const canOpenVersion = !!entry.versionId;
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      className={`reports-sidebar-edit-summary${canOpenVersion ? " is-clickable" : ""}${selectedVersionId === entry.versionId ? " is-active" : ""}`}
                      onClick={() => {
                        if (entry.versionId) {
                          closeReportEditor();
                          setSelectedVersionId(String(entry.versionId));
                        }
                      }}
                      disabled={!canOpenVersion}
                    >
                      <div className="reports-sidebar-edit-summary-avatar-wrap">
                        {entry.actorImageUrl ? (
                          <img src={entry.actorImageUrl} alt="" className="reports-sidebar-avatar-img" />
                        ) : (
                          <span className={`${auditAvatarClass(person)} reports-sidebar-edit-summary-avatar`}>
                            {auditInitials(person, entry.actorEmail)}
                          </span>
                        )}
                      </div>
                      <div className="reports-sidebar-edit-summary-copy">
                        <p>
                          <strong>{person}</strong>{" "}
                          <span>edited the report</span>
                        </p>
                        <time>
                          {formatAuditTime(entry.createdAt)} · {formatAuditDate(entry.createdAt)}
                        </time>
                      </div>
                      <span className="reports-sidebar-edit-summary-chevron" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="m9 18 6-6-6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </button>
                  );
                })
              )
            ) : (
              <div className="reports-sidebar-comments">
                <div className="reports-sidebar-comments-thread" ref={commentsThreadRef}>
                  {commentsError ? (
                    <div className="reports-sidebar-comment-error">{commentsError}</div>
                  ) : null}
                  {commentsLoading ? (
                    <div className="reports-empty">Loading comments from TrackTik...</div>
                  ) : reportComments.length === 0 ? (
                    <div className="reports-sidebar-card reports-sidebar-card--comment-empty">
                      <p>No comments yet.</p>
                      <span>Comments posted here are sent to TrackTik and only appear after TrackTik confirms them.</span>
                    </div>
                  ) : (
                    reportComments.map((comment) => {
                      const person =
                        comment.argusAuthorName ||
                        comment.trackTikAuthorName ||
                        comment.authorName ||
                        comment.argusAuthorEmail ||
                        comment.trackTikAuthorEmail ||
                        comment.authorEmail ||
                        "TrackTik user";
                      const email = comment.argusAuthorEmail || comment.trackTikAuthorEmail || comment.authorEmail;
                      const authorImageUrl = comment.argusAuthorImageUrl || comment.trackTikAuthorImageUrl;
                      return (
                        <div key={comment.id} className="reports-sidebar-card reports-sidebar-card--comment">
                          <div className="reports-sidebar-person-row">
                            <div
                              className={`reports-sidebar-avatar ${
                                authorImageUrl ? "reports-sidebar-avatar--photo" : auditAvatarClass(person)
                              }`}
                            >
                              {authorImageUrl ? (
                                <img src={authorImageUrl} alt="" className="reports-sidebar-avatar-img" />
                              ) : (
                                auditInitials(person, email)
                              )}
                            </div>
                            <div className="reports-sidebar-person">
                              <p>{person}</p>
                              {email ? <span>{email}</span> : null}
                            </div>
                          </div>
                          <p className="reports-sidebar-comment-content">{comment.content}</p>
                          <div className="reports-sidebar-meta">
                            <span>Commented {formatAuditDate(comment.createdAt || comment.createdOn)}</span>
                            <span>{formatAuditTime(comment.createdAt || comment.createdOn)}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <form
                  className="reports-sidebar-comment-box"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void handlePostComment();
                  }}
                >
                  <input
                    type="text"
                    placeholder="Add a comment..."
                    value={commentDraft}
                    onChange={(event) => setCommentDraft(event.target.value)}
                    disabled={isPostingComment || !selectedReportId}
                  />
                  <button
                    type="submit"
                    disabled={isPostingComment || !commentDraft.trim() || !selectedReportId}
                    aria-label="Send comment"
                  >
                    <svg
                      className="reports-sidebar-send-icon"
                      viewBox="0 0 176.368 176.368"
                      width="12"
                      height="12"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <g>
                        <g>
                          <path
                            clipRule="evenodd"
                            d="m175.865 12.489c2.469-7.408-4.578-14.456-11.986-11.987l-157.399 52.467c-8.839 2.946-8.565 15.542.393 18.101l76.552 21.873 21.872 76.552c2.56 8.959 15.155 9.233 18.101.393zm-12.34 7.055-49.116 147.348-21.83-76.403zm-6.701-6.701-70.945 70.945-76.402-21.829z"
                            fill="#ffffff"
                            fillRule="evenodd"
                          />
                        </g>
                      </g>
                    </svg>
                  </button>
                </form>
              </div>
            )}
          </div>
        </aside>
        )}

        {selectedReportId ? (
        <main className="reports-detail-panel">
          {isAssign ? (
            <div className="reports-assign-actionbar">
              <div className="reports-assign-actionbar-status">
                {(() => {
                  const meta = assignmentStatusMeta(
                    selectedWorkItem?.workStatus,
                    selectedWorkItem?.escalationReason
                  );
                  return <span className={`reports-assign-pill ${meta.cls}`}>{meta.label}</span>;
                })()}
                {selectedWorkItem?.claimedByName ? (
                  <span className="reports-assign-claimed">Claimed by {selectedWorkItem.claimedByName}</span>
                ) : null}
              </div>
              <div className="reports-assign-actionbar-buttons">
                <button
                  type="button"
                  className={`reports-assign-btn is-review${
                    selectedWorkItem?.workStatus === "in_review" || selectedWorkItem?.workStatus === "reviewing"
                      ? " is-active"
                      : ""
                  }`}
                  disabled={assignSavingStatus !== null}
                  onClick={() => selectedReportId && handleAssignmentStatus(selectedReportId, "in_review")}
                >
                  {assignSavingStatus === "in_review" ? "…" : "In Review"}
                </button>
                <button
                  type="button"
                  className={`reports-assign-btn is-complete${
                    selectedWorkItem?.workStatus === "completed" ? " is-active" : ""
                  }`}
                  disabled={assignSavingStatus !== null}
                  onClick={() => selectedReportId && handleAssignmentStatus(selectedReportId, "completed")}
                >
                  {assignSavingStatus === "completed" ? "…" : "Completed"}
                </button>
                <div className="reports-assign-escalate">
                  <button
                    type="button"
                    className={`reports-assign-btn is-escalate${
                      selectedWorkItem?.workStatus === "escalated" ? " is-active" : ""
                    }`}
                    disabled={assignSavingStatus !== null}
                    aria-haspopup="menu"
                    aria-expanded={escalateMenuOpen}
                    onClick={() => setEscalateMenuOpen((open) => !open)}
                  >
                    Escalate
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                      <path d="M1.5 3.5 L5 7 L8.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  {escalateMenuOpen ? (
                    <div className="reports-assign-escalate-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => selectedReportId && handleAssignmentStatus(selectedReportId, "escalated", "needs_photos")}
                      >
                        Needs photos
                        <span>No photos / body cam — supervisor review</span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => selectedReportId && handleAssignmentStatus(selectedReportId, "escalated", "clarification")}
                      >
                        Clarification
                        <span>Unsure what to do with this report</span>
                      </button>
                    </div>
                  ) : null}
                </div>
                {selectedWorkItem && selectedWorkItem.workStatus !== "new" ? (
                  <button
                    type="button"
                    className="reports-assign-btn is-reset"
                    disabled={assignSavingStatus !== null}
                    onClick={() => selectedReportId && handleAssignmentStatus(selectedReportId, "new")}
                  >
                    Reopen
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
          {selectedReportId && (detail === undefined || (selectedVersionId && versionDetail === undefined)) ? (
            <div className="reports-detail-empty">Loading report...</div>
          ) : !selectedReport ? (
            <div className="reports-detail-empty">
              <div className="reports-detail-empty-title">No report selected</div>
              <p>Import or select a TrackTik report to preview the Argus-hosted version.</p>
            </div>
          ) : (
            <>
              {isViewingHistoricalVersion ? (
                <div className="reports-version-banner">
                  <span>
                    Viewing previous version
                    {versionDetail?.version?.versionNumber ? ` #${versionDetail.version.versionNumber}` : ""}
                  </span>
                  <button type="button" onClick={() => setSelectedVersionId(null)}>
                    Back to current report
                  </button>
                </div>
              ) : null}
              <section className="reports-pdf-sheet">
                <div className="reports-pdf-top">
                  <div className="reports-pdf-identity">
                    <img src={apsLogo} alt="Alpine Protective Solutions" />
                    <div className="reports-pdf-address">
                      <span>602 14th Street Modesto, Califronia</span>
                      <span>95354 United States of America</span>
                    </div>
                  </div>

                  <div className="reports-pdf-meta">
                    <div className="reports-pdf-meta-title">
                      {selectedReport.templateName || selectedReport.categoryName || "TrackTik Report"}
                    </div>
                    <div className="reports-pdf-meta-row">
                      <span>Report #</span>
                      <strong>{selectedReport.trackTikId}</strong>
                    </div>
                    <div className="reports-pdf-meta-row">
                      <span>Report Date</span>
                      <strong>{formatDateOnly(reportDateValue)}</strong>
                    </div>
                    <div className="reports-pdf-meta-row">
                      <span>Report Time</span>
                      <strong>{formatTimeOnly(reportDateValue)}</strong>
                    </div>
                    <div className="reports-pdf-meta-row">
                      <span>Opened by scanning</span>
                      <strong>{openedByScanning || selectedReport.checkpointName || "Not recorded"}</strong>
                    </div>
                    <div className="reports-pdf-meta-row">
                      <span>Created By</span>
                      <strong>{selectedReport.officerName || "Unknown officer"}</strong>
                    </div>
                    <div className="reports-pdf-meta-row">
                      <span>Position</span>
                      <strong>{selectedReport.positionName || "Not recorded"}</strong>
                    </div>
                    <div className="reports-pdf-meta-row">
                      <span>Client</span>
                      <strong>{selectedReport.accountName || selectedReport.siteName || "Not recorded"}</strong>
                    </div>
                  </div>
                </div>

                {activeDetail.fields.length === 0 ? (
                  <div className="reports-empty">No rendered fields were found in the imported schema.</div>
                ) : (
                  <div className="reports-info-table">
                    <div className="reports-info-table-title reports-info-table-title--with-actions">
                      <span>Information</span>
                      {!isViewingHistoricalVersion ? (
                        <button
                          type="button"
                          className="reports-edit-toggle"
                          onClick={() => {
                            if (isEditMode) {
                              closeReportEditor();
                            } else {
                              openReportEditor();
                            }
                          }}
                        >
                          {isEditMode ? "Close editor" : "Edit report"}
                        </button>
                      ) : null}
                    </div>
                    {editMessage ? <div className="reports-edit-message">{editMessage}</div> : null}
                    {visibleFields.map((field) => {
                      const attachedMedia = (field.mediaKeys || [])
                        .map((key) => mediaByKey.get(key))
                        .filter(Boolean) as ReportMedia[];
                      const fieldValue = safeFieldValue(field);
                      return (
                        <div key={field.fieldKey} className="reports-info-row">
                          <div className="reports-info-label">{field.label}</div>
                          <div className="reports-info-value">
                            {fieldValue && attachedMedia.length === 0 ? (
                              <div className="reports-field-value">{fieldValue}</div>
                            ) : attachedMedia.length === 0 ? (
                              <div className="reports-field-value reports-field-value--empty">
                                {field.isMediaField ? "No media attached" : "No value recorded"}
                              </div>
                            ) : null}
                            {attachedMedia.length ? (
                              <div className="reports-media-grid reports-media-grid--table">
                                {attachedMedia.map((media) => (
                                  <MediaPreview key={media.mediaKey} media={media} hideCaption />
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {unattachedMedia.length ? (
                <section className="reports-pdf-sheet reports-pdf-sheet--compact">
                  <div className="reports-info-table">
                    <div className="reports-info-table-title">Additional Media</div>
                    <div className="reports-info-row">
                      <div className="reports-info-label">Media</div>
                      <div className="reports-info-value">
                        <div className="reports-media-grid reports-media-grid--table">
                    {unattachedMedia.map((media) => (
                      <MediaPreview key={media.mediaKey} media={media} hideCaption />
                    ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}
            </>
          )}
        </main>
        ) : null}
      </div>
      {isEditMode ? (
        <div className="rep-modal-backdrop">
          <div
            className="rep-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Edit report"
          >
            <header className="rep-modal-head">
              <div className="rep-modal-head-text">
                <h3>Edit Report</h3>
                <span className="rep-modal-head-id">#{selectedReportId}</span>
              </div>
              <span
                className={`rep-modal-head-changes${changedReportEditCount ? " is-active" : ""}`}
              >
                <span className="rep-modal-head-status-dot" aria-hidden="true" />
                {changedReportEditCount
                  ? `${changedReportEditCount} unsaved`
                  : "No changes"}
              </span>
            </header>
            <div className={`rep-modal-body${isSavingReportEdits ? " is-saving" : ""}`}>
              {isSavingReportEdits ? (
                <div className="rep-save-loading" role="status" aria-live="polite">
                  <div className="rep-save-spinner" aria-hidden="true" />
                  <div className="rep-save-loading-text">
                    <span className="rep-save-loading-kicker">Sending to TrackTik</span>
                    <strong>Saving report changes</strong>
                    <span>Verifying TrackTik accepted the update before Argus refreshes.</span>
                  </div>
                </div>
              ) : (
                <div className="rep-editor-layout">
                  <aside className="rep-editor-side">
                    {editorInformationFields.length ? (
                      <div className="rep-side-fields">
                        {editorInformationFields.map((field) => {
                          const typedKind = typedEditKind(field);
                          const draft = fieldDrafts[field.fieldKey] ?? reportFieldInitialDraft(field);
                          const typedOptions = typedKind ? typedOptionsForKind(typedKind) : undefined;
                          const isChanged = draft !== reportFieldInitialDraft(field);
                          const isRequiredEmpty = !!field.required && draft.trim().length === 0;
                          const isSelectKind =
                            typedKind === "account" ||
                            typedKind === "client" ||
                            typedKind === "position" ||
                            typedKind === "reportFlag";
                          const setDraft = (next: string) =>
                            setFieldDrafts((current) => ({
                              ...current,
                              [field.fieldKey]: next,
                            }));
                          return (
                            <div
                              key={field.fieldKey}
                              className={`rep-field${typedKind === "date" || typedKind === "time" ? " rep-field--half" : " rep-field--full"}${isChanged ? " is-changed" : ""}${isRequiredEmpty ? " is-error" : ""}`}
                            >
                              <label className="rep-field-label">
                                <span>{field.label}</span>
                                {field.required ? (
                                  <span className="rep-row-required" aria-label="required">
                                    *
                                  </span>
                                ) : null}
                              </label>
                              <div className="rep-field-control">
                                {typedKind === "date" ? (
                                  <DateField
                                    value={draft}
                                    invalid={isRequiredEmpty}
                                    onChange={(next) => setDraft(next)}
                                  />
                                ) : typedKind === "time" ? (
                                  <TimeField
                                    value={draft}
                                    invalid={isRequiredEmpty}
                                    onChange={(next) => setDraft(next)}
                                  />
                                ) : typedKind === "datetime" ? (
                                  <DateTimeField
                                    value={draft}
                                    invalid={isRequiredEmpty}
                                    onChange={(next) => setDraft(next)}
                                  />
                                ) : isSelectKind ? (
                                  <SearchableSelect
                                    value={draft}
                                    options={
                                      typedOptions
                                        ? typedOptions.map((option) => ({
                                            value: String(option.value),
                                            label: option.label,
                                            subtitle: option.subtitle,
                                          }))
                                        : undefined
                                    }
                                    loading={typedOptions === undefined}
                                    invalid={isRequiredEmpty}
                                    placeholder={`Select ${field.label.toLowerCase()}`}
                                    searchPlaceholder={`Search ${field.label.toLowerCase()}...`}
                                    onChange={(next) => setDraft(next)}
                                  />
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                    {editorPhotoFields.length ? (
                      <div className="rep-side-photos">
                        <div className="rep-section-head">
                          <div className="rep-section-label">Photos</div>
                          <div className="rep-section-count">{editorPhotoFilledCount} of {editorPhotoTotalCount}</div>
                        </div>
                        <div className="rep-photo-tiles">
                          {editorPhotoFields.map((field, index) => {
                            const attachedMedia = (field.mediaKeys || [])
                              .map((key) => mediaByKey.get(key))
                              .filter(Boolean) as ReportMedia[];
                            const photoDraft = photoDrafts[field.fieldKey];
                            const isDraftRemoved = photoDraft?.operation === "remove";
                            const displayMedia = isDraftRemoved ? [] : attachedMedia;
                            const isSavingPhoto = savingPhotoFieldKey === field.fieldKey;
                            const canUploadPhoto = !!field.trackTikReportFieldId || !!field.trackTikTemplateFieldId;
                            const photoOperation = field.trackTikReportFieldId ? "replace" : "add";
                            const primaryMedia =
                              displayMedia.find((media) => !!media.storageUrl) || displayMedia[0];
                            const previewUrl = photoDraft?.operation === "add" || photoDraft?.operation === "replace"
                              ? photoDraft.previewUrl
                              : undefined;
                            const canRemove =
                              (!!previewUrl && photoDraft?.operation === "add") ||
                              (attachedMedia.length > 0 && !field.required && !!field.trackTikReportFieldId && !isDraftRemoved);
                            const displayLabel = normalizePhotoLabel(field.label);
                            const photoNumber = displayLabel.match(/\d+/)?.[0] || String(index + 1);
                            const hasImage =
                              !!previewUrl || (!!primaryMedia && !!primaryMedia.storageUrl && mediaIsImage(primaryMedia));
                            const isFilled = !!previewUrl || displayMedia.length > 0;
                            const clearOrRemovePhoto = () => {
                              if (photoDraft?.operation === "add") {
                                handleClearStagedPhoto(field);
                              } else {
                                handleRemovePhoto(field);
                              }
                            };
                            return (
                              <div
                                key={field.fieldKey}
                                className={`rep-photo-card${isFilled ? " is-filled" : ""}${isSavingPhoto ? " is-saving" : ""}${field.required && !isFilled ? " is-required" : ""}${photoDraft ? " is-pending" : ""}`}
                              >
                                <label className="rep-photo-tile rep-photo-tile--grid">
                                  <span className="rep-photo-number">{photoNumber}</span>
                                  {hasImage ? (
                                    <img
                                      className="rep-photo-img"
                                      src={previewUrl || primaryMedia!.storageUrl!}
                                      alt={displayLabel}
                                      loading="lazy"
                                    />
                                  ) : displayMedia.length ? (
                                    <span className="rep-photo-file" aria-hidden="true">
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path
                                          d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
                                          stroke="currentColor"
                                          strokeWidth="1.6"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M14 3v5h5"
                                          stroke="currentColor"
                                          strokeWidth="1.6"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </span>
                                  ) : (
                                    <span className="rep-photo-plus" aria-hidden="true">+</span>
                                  )}
                                  <span className="rep-photo-overlay">
                                    {isSavingPhoto
                                      ? "···"
                                      : isFilled
                                        ? "Replace"
                                        : "Add photo"}
                                  </span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    disabled={isSavingPhoto || !canUploadPhoto}
                                    onChange={(event) =>
                                      void handlePhotoUpload(field, event, photoOperation)
                                    }
                                  />
                                </label>
                                {canRemove ? (
                                  <button
                                    type="button"
                                    className="rep-photo-x"
                                    aria-label={`Remove ${displayLabel}`}
                                    disabled={isSavingPhoto}
                                    onClick={clearOrRemovePhoto}
                                  >
                                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                                      <path
                                        d="M2 2 L8 8 M8 2 L2 8"
                                        stroke="currentColor"
                                        strokeWidth="1.4"
                                        strokeLinecap="round"
                                      />
                                    </svg>
                                  </button>
                                ) : null}
                              </div>
                            );
                          })}
                          {Array.from({ length: Math.max(0, 4 - editorPhotoFields.length) }).map((_, index) => (
                            <div key={`photo-placeholder-${index}`} className="rep-photo-placeholder" aria-hidden="true" />
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </aside>
                  <main className="rep-editor-main">
                    <div className="rep-narrative-head">
                      <div className="rep-narrative-title">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M6 3h9l4 4v14H6z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                          <path d="M15 3v5h4M9 13h6M9 17h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                        <span>Narrative</span>
                      </div>
                      {editorDetailFields.length ? (
                        <button
                          type="button"
                          className="rep-reformat-btn"
                          onClick={() => void handleReformatReport()}
                          disabled={isReformattingReport || isSavingReportEdits}
                        >
                          <span aria-hidden="true">✦</span>
                          {isReformattingReport ? "Reformatting" : "Reformat Report"}
                        </button>
                      ) : null}
                    </div>
                    {editorDetailFields.length ? (
                      <div className="rep-details-stack">
                        {editorDetailFields.map((field) => {
                          const draft = fieldDrafts[field.fieldKey] ?? reportFieldInitialDraft(field);
                          const isChanged = draft !== reportFieldInitialDraft(field);
                          const isRequiredEmpty = !!field.required && draft.trim().length === 0;
                          const setDraft = (next: string) =>
                            setFieldDrafts((current) => ({
                              ...current,
                              [field.fieldKey]: next,
                            }));
                          return (
                            <div
                              key={field.fieldKey}
                              className={`rep-field rep-field--full${isChanged ? " is-changed" : ""}${isRequiredEmpty ? " is-error" : ""}`}
                            >
                              <label className="rep-field-label">
                                <span>{field.label}</span>
                                {field.required ? (
                                  <span className="rep-row-required" aria-label="required">
                                    *
                                  </span>
                                ) : null}
                              </label>
                              <textarea
                                className={`rep-textarea${isRequiredEmpty ? " is-invalid" : ""}`}
                                value={draft}
                                onChange={(event) => setDraft(event.target.value)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rep-empty">No editable narrative fields were found for this report.</div>
                    )}
                  </main>
                </div>
              )}
            </div>
            <footer className="rep-modal-foot">
              <span
                className={`rep-modal-status${requiredReportEditField || requiredPhotoEditField ? " is-error" : ""}${changedReportEditCount && !requiredReportEditField && !requiredPhotoEditField ? " is-active" : ""}`}
              >
                <span className="rep-modal-status-dot" aria-hidden="true" />
                {isSavingReportEdits
                  ? "Sending changes to TrackTik"
                  : isReformattingReport
                    ? "Reformatting report text"
                  : requiredReportEditField
                  ? `${requiredReportEditField.label} required`
                  : requiredPhotoEditField
                    ? `${normalizePhotoLabel(requiredPhotoEditField.label)} required`
                  : reportEditOptionsLoading
                    ? "Loading options"
                    : changedReportEditCount
                      ? `${changedReportEditCount} change${changedReportEditCount === 1 ? "" : "s"} will be saved`
                      : "No changes yet"}
              </span>
              {!isSavingReportEdits ? (
              <div className="rep-modal-actions">
                <button
                  type="button"
                  className="rep-btn rep-btn--ghost"
                  onClick={closeReportEditor}
                  disabled={isSavingReportEdits || isReformattingReport}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rep-btn rep-btn--primary"
                  disabled={
                    isSavingReportEdits ||
                    isReformattingReport ||
                    !changedReportEditCount ||
                    !!requiredReportEditField ||
                    !!requiredPhotoEditField ||
                    reportEditOptionsLoading
                  }
                  onClick={() => void handleSaveReportEdits()}
                >
                  {isSavingReportEdits ? "Saving" : "Save"}
                </button>
              </div>
              ) : null}
            </footer>
          </div>
        </div>
      ) : null}
      {activePreview && previewAnchor ? (
        <div
          className="reports-preview-popover reports-preview-popover--floating"
          role="tooltip"
          style={{
            top: `${previewAnchor.top}px`,
            left: `${previewAnchor.left}px`,
            transformOrigin:
              previewAnchor.placement === "right" ? "left center" : "right center",
          }}
          onMouseEnter={() => {
            setPreviewReportId(activePreview.reportId);
          }}
          onMouseLeave={() => {
            setPreviewReportId((current) =>
              current === activePreview.reportId ? null : current
            );
          }}
        >
          <span className="reports-mini-sheet">
            <span className="reports-mini-top">
              <span className="reports-mini-brand">
                <img src={apsLogo} alt="APS" />
                <span>602 14th Street Modesto, CA</span>
                <span>95354 United States of America</span>
              </span>
              <span className="reports-mini-meta">
                <strong>{activePreview.previewType}</strong>
                <span><em>Report #</em><b>{activePreview.reportId}</b></span>
                <span><em>Date</em><b>{formatDateOnly(activePreview.previewDate)}</b></span>
                <span><em>Time</em><b>{formatTimeOnly(activePreview.previewDate)}</b></span>
              </span>
            </span>
            <span className="reports-mini-info">
              <span className="reports-mini-title">Information</span>
              <span><em>Account</em><b>{activePreview.accountName}</b></span>
              <span><em>Reported By</em><b>{activePreview.officerName}</b></span>
              {activePreview.previewVisibleFields.map((field) => (
                <span key={field.fieldKey}>
                  <em>{field.label}</em>
                  <b>{safeFieldValue(field)}</b>
                </span>
              ))}
            </span>
            {activePreview.previewDescription ? (
              <span className="reports-mini-description">{activePreview.previewDescription}</span>
            ) : null}
            {activePreview.previewImages.length ? (
              <span className="reports-mini-photos">
                {activePreview.previewImages.map((media, index) => (
                  <span key={media.mediaKey}>
                    {media.storageUrl ? (
                      <img src={media.storageUrl} alt={media.fieldLabel || `Report photo ${index + 1}`} />
                    ) : null}
                  </span>
                ))}
              </span>
            ) : null}
          </span>
          {activePreview.isLoading ? (
            <span className="reports-preview-loading">Loading report preview...</span>
          ) : null}
        </div>
      ) : null}

      {isAssign && showDispatchers ? (
        <div
          className="reports-assign-modal-scrim"
          role="dialog"
          aria-modal="true"
          aria-label="Assign dispatcher shifts"
          onClick={() => setShowDispatchers(false)}
        >
          <div className="reports-assign-modal" onClick={(event) => event.stopPropagation()}>
            <div className="reports-assign-modal-head">
              <div>
                <div className="reports-assign-modal-eyebrow">Admin</div>
                <h3>Dispatcher shifts</h3>
              </div>
              <button
                type="button"
                className="reports-assign-modal-close"
                onClick={() => setShowDispatchers(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="reports-assign-modal-sub">
              Assign each dispatcher to a shift. They'll only see reports routed to that shift's queue.
            </p>
            <div className="reports-assign-modal-list">
              {dispatchers === undefined ? (
                <div className="reports-assign-modal-empty">Loading dispatchers…</div>
              ) : dispatchers.length === 0 ? (
                <div className="reports-assign-modal-empty">No user profiles found yet.</div>
              ) : (
                dispatchers.map((dispatcher) => (
                  <div key={dispatcher.userId} className="reports-assign-modal-row">
                    <div className="reports-assign-modal-person">
                      <span className="reports-assign-modal-name">
                        {dispatcher.name || dispatcher.email || dispatcher.userId}
                      </span>
                      {dispatcher.email ? (
                        <span className="reports-assign-modal-email">{dispatcher.email}</span>
                      ) : null}
                    </div>
                    <select
                      className="reports-assign-modal-select"
                      value={dispatcher.shift || ""}
                      onChange={(event) => void handleAssignShift(dispatcher, event.target.value)}
                    >
                      <option value="">Unassigned</option>
                      {(dispatchContext?.shifts || []).map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MediaPreview({ media, hideCaption = false }: { media: ReportMedia; hideCaption?: boolean }) {
  const label = media.fieldLabel || media.fileName || "Report media";
  if (media.downloadStatus === "failed") {
    return (
      <div className="reports-media-card reports-media-card--failed">
        <div className="reports-media-label">{label}</div>
        <div className="reports-media-error">{media.downloadError || "Media download failed"}</div>
      </div>
    );
  }
  if (media.storageUrl && mediaIsImage(media)) {
    return (
      <figure className="reports-media-card">
        <img src={media.storageUrl} alt={label} loading="lazy" />
        {!hideCaption ? (
          <figcaption>
            <span>{label}</span>
            <span>{formatBytes(media.sizeBytes)}</span>
          </figcaption>
        ) : null}
      </figure>
    );
  }
  return (
    <a className="reports-media-card reports-media-link" href={media.storageUrl || "#"} target="_blank" rel="noreferrer">
      <div className="reports-media-label">{label}</div>
      <div>{media.mimeType || "Stored file"} {formatBytes(media.sizeBytes)}</div>
    </a>
  );
}
